import React, { forwardRef, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import * as faceapi from 'face-api.js';
import ws from '../services/websocket';
import { apiFetch } from '../services/api';
import { resolveApiBaseUrl, resolveWebsocketUrl } from '../constants';
import { captureJpegFromStream } from '../../../../src/security/screenCaptureManager';
import {
  SYSTEM_CHECK_MINUTES_BEFORE_EXAM_DEFAULT,
  conservativeNowMs,
  describeExamSchedule,
  hasExamStarted,
  isEnvironmentCheckPeriod,
  isProctoringSessionPeriod,
  msUntilSystemCheckWindow,
  normalizeSystemCheckMinutesBefore,
  parseExamTimestamp,
  formatExamTimestamp,
} from '../examTiming';

const SPINNER_CSS = '@keyframes pw-spin{to{transform:rotate(360deg)}}.pw-spinner{animation:pw-spin 0.8s linear infinite}';

// Fallback when the client mount has no z-index: above normal exam UI, below typical modal layers (~1300).
const PANEL_Z_FALLBACK = 1100;

const FLOATING_PANEL_FIXED_BASE = {
  position: 'fixed',
  top: 72,
  right: 16,
  pointerEvents: 'auto',
  maxHeight: '85vh',
  overflow: 'auto',
  background: '#fff',
  color: '#000',
  opacity: 1,
  visibility: 'visible',
};

const OFFSCREEN_MEDIA_STYLE = {
  position: 'fixed',
  left: -10000,
  top: -10000,
  width: 1,
  height: 1,
  opacity: 0,
  pointerEvents: 'none',
  visibility: 'hidden',
};

// Zero-size shell — never captures clicks. Panels always portal to document.body.
const MOUNT_SHELL_STYLE = {
  position: 'absolute',
  width: 0,
  height: 0,
  overflow: 'visible',
  pointerEvents: 'none',
};

function parseCssZIndex(value) {
  if (value == null || value === '' || value === 'auto') return null;
  const n = Number.parseInt(String(value), 10);
  return Number.isFinite(n) ? n : null;
}

/** z-index from the client's mount placement (so we don't force above announcements). */
function resolveHostZIndex(el) {
  if (!el || typeof window === 'undefined') return null;
  let node = el.parentElement || el;
  while (node && node !== document.documentElement) {
    try {
      const inline = parseCssZIndex(node.style && node.style.zIndex);
      if (inline != null) return inline;
      const style = window.getComputedStyle(node);
      const computed = parseCssZIndex(style.zIndex);
      if (computed != null && style.position !== 'static') return computed;
    } catch (_) { /* ignore */ }
    node = node.parentElement;
  }
  return null;
}

/** True when the client wraps the widget in a near-viewport fixed/absolute overlay. */
function isFullScreenMountWrapper(el) {
  if (!el || typeof window === 'undefined') return false;
  try {
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth || document.documentElement.clientWidth || 0;
    const vh = window.innerHeight || document.documentElement.clientHeight || 0;
    if (!vw || !vh) return false;
    const pos = window.getComputedStyle(el).position;
    return (
      (pos === 'fixed' || pos === 'absolute')
      && rect.width >= vw * 0.8
      && rect.height >= vh * 0.8
    );
  } catch (_) {
    return false;
  }
}

/**
 * Collect positioned ancestors that sit above the exam and would swallow clicks.
 * Always includes the direct mount parent when it is fixed/absolute (common LMS pattern),
 * plus any near-fullscreen overlay ancestors.
 */
function collectClickThroughOverlays(host) {
  const list = [];
  if (!host || typeof document === 'undefined') return list;
  let node = host.parentElement;
  let depth = 0;
  while (node && node !== document.body && node !== document.documentElement && depth < 8) {
    try {
      const pos = window.getComputedStyle(node).position;
      const isPositioned = pos === 'fixed' || pos === 'absolute';
      if (isPositioned && (depth === 0 || isFullScreenMountWrapper(node))) {
        list.push(node);
      }
    } catch (_) { /* ignore */ }
    node = node.parentElement;
    depth += 1;
  }
  return list;
}

function withZIndex(style, zIndex) {
  if (zIndex == null) return style;
  return { ...style, zIndex };
}

const FACE_VERIFY_INTERVAL_MS_DEFAULT = 30 * 1000;
const NO_FACE_TIMEOUT_MS_DEFAULT = 15 * 1000;
const FACE_DETECTION_SCORE_THRESHOLD_DEFAULT = 0.35;
const FACE_VERIFY_FAILURES_BEFORE_VIOLATION_DEFAULT = 1;
const NOISE_THRESHOLD_DEFAULT = 45;
const NOISE_CONSECUTIVE_READINGS_DEFAULT = 4;
const NOISE_VIOLATION_COOLDOWN_MS = 60000;
const HIGH_LIGHT_THRESHOLD_DEFAULT = 165;

function createClientViolationId() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch (_) { /* ignore */ }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function averageFrameBrightness(imageData) {
  const data = imageData.data;
  let total = 0;
  for (let i = 0; i < data.length; i += 4) {
    total += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
  }
  return total / (data.length / 4);
}

function measureAudioLevel(analyser, frequencyBuffer, timeDomainBuffer) {
  analyser.getByteFrequencyData(frequencyBuffer);
  let freqSum = 0;
  let freqMax = 0;
  for (let i = 0; i < frequencyBuffer.length; i++) {
    freqSum += frequencyBuffer[i];
    if (frequencyBuffer[i] > freqMax) freqMax = frequencyBuffer[i];
  }
  const freqAvg = frequencyBuffer.length > 0 ? freqSum / frequencyBuffer.length : 0;

  analyser.getByteTimeDomainData(timeDomainBuffer);
  let sumSquares = 0;
  let peak = 0;
  for (let i = 0; i < timeDomainBuffer.length; i++) {
    const sample = Math.abs((timeDomainBuffer[i] - 128) / 128);
    if (sample > peak) peak = sample;
    sumSquares += sample * sample;
  }
  const rms = Math.sqrt(sumSquares / timeDomainBuffer.length) * 255;
  const peakScaled = peak * 255;
  return Math.max(freqAvg, rms, peakScaled, freqMax);
}

async function ensureAudioContextRunning(audioContext) {
  if (!audioContext) return false;
  if (audioContext.state === 'running') return true;
  try {
    await audioContext.resume();
  } catch (_) { /* ignore */ }
  return audioContext.state === 'running';
}

function hasSpeechRecognitionSupport() {
  if (typeof window === 'undefined') return false;
  return 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
}

async function waitForVideoReady(getVideoEl, timeoutMs = 10000) {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const vid = getVideoEl();
    if (vid && vid.videoWidth > 0 && vid.videoHeight > 0) return vid;
    await new Promise((r) => setTimeout(r, 250));
  }
  return getVideoEl();
}

const SCREEN_CAPTURE_LABEL = /screen|window|display|monitor|web contents|\btab\b/i;

function trackLooksLikeScreenCapture(track) {
  if (!track || track.kind !== 'video') return false;
  try {
    const settings = typeof track.getSettings === 'function' ? track.getSettings() : null;
    if (settings && settings.displaySurface) return true;
  } catch (_) { /* ignore */ }
  const label = track.label || '';
  return SCREEN_CAPTURE_LABEL.test(label);
}

async function detectActiveScreenCapture(extraStreams = []) {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    if (devices.some((d) => d.label && SCREEN_CAPTURE_LABEL.test(d.label))) {
      return { detected: true, reason: 'device-label' };
    }
  } catch (_) { /* ignore */ }

  const seen = new Set();
  const streams = [];
  extraStreams.forEach((s) => { if (s) streams.push(s); });
  if (typeof document !== 'undefined') {
    document.querySelectorAll('video').forEach((el) => {
      if (el.srcObject instanceof MediaStream) streams.push(el.srcObject);
    });
  }
  for (const stream of streams) {
    if (seen.has(stream)) continue;
    seen.add(stream);
    for (const track of stream.getVideoTracks()) {
      if (trackLooksLikeScreenCapture(track)) {
        return { detected: true, reason: 'media-track' };
      }
    }
  }

  try {
    if (navigator.permissions && typeof navigator.permissions.query === 'function') {
      const status = await navigator.permissions.query({ name: 'display-capture' });
      if (status && status.state === 'granted') {
        return { detected: true, reason: 'display-capture-permission' };
      }
    }
  } catch (_) { /* ignore — not supported in all browsers */ }

  return { detected: false, reason: null };
}

const ProctoringWidget = forwardRef(function ProctoringWidget(
  {
    userId,
    examId,
    adminExamId,
    libraryId,
    websocketUrl,
    apiBaseUrl,
    start,
    runSystemCheck,
    requireSystemCheck,
    onRegistrationStatus,
    onSystemCheckComplete,
    onViolation,
    onTranscriptUpdate,
    sendAdminFeed,
    uploadSnapshots,
    snapshotOnViolation,
    modelsBaseUrl,
    examIsActive,
    sendViolationsToServer,
    sendTranscriptsToServer,
    examStartAt,
    endExam = false,
    systemCheckMinutesBeforeExam = SYSTEM_CHECK_MINUTES_BEFORE_EXAM_DEFAULT,
    showDebug = false,
    showVideo = false,
    onReadyForMonitoring,
    onStartMonitoringClick,
    enableNoiseMonitoring = true,
    noiseThreshold = NOISE_THRESHOLD_DEFAULT,
    noiseConsecutiveReadings = NOISE_CONSECUTIVE_READINGS_DEFAULT,
    noiseCheckInterval = 1000,
    enableLightingMonitoring = true,
    lowLightThreshold = 50,
    highLightThreshold = HIGH_LIGHT_THRESHOLD_DEFAULT,
    lightingCheckInterval = 5000,
    faceDetectionScoreThreshold = FACE_DETECTION_SCORE_THRESHOLD_DEFAULT,
    faceDetectionIntervalMs = 5000,
    noFaceTimeoutMs = NO_FACE_TIMEOUT_MS_DEFAULT,
    faceVerifyIntervalMs = FACE_VERIFY_INTERVAL_MS_DEFAULT,
    faceVerifyFailuresBeforeViolation = FACE_VERIFY_FAILURES_BEFORE_VIOLATION_DEFAULT,
    iacId,
    screenShareStream = null,
  },
  ref
) {
  const videoRef = useRef(null);
  const previewVideoRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const regStreamRef = useRef(null);
  const started = useRef(false);
  const recognitionRef = useRef(null);
  const recognitionStartingRef = useRef(false);
  const lastFinalTranscriptRef = useRef('');
  const transcriptRetryRef = useRef({ retries: 0 });
  const [isStreaming, setIsStreaming] = useState(false);
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [modelDiag, setModelDiag] = useState({ base: '', startedAt: 0, endedAt: 0, success: false, error: '', manifestStatus: null });
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [registrationDismissed, setRegistrationDismissed] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [isExcludedFromMonitoring, setIsExcludedFromMonitoring] = useState(false);
  const [systemCheckOpenForRegistered, setSystemCheckOpenForRegistered] = useState(false);
  const [environmentCheckDismissed, setEnvironmentCheckDismissed] = useState(false);
  const [environmentCheckCompleted, setEnvironmentCheckCompleted] = useState(false);
  const [regSuccessOpen, setRegSuccessOpen] = useState(false);
  const [regStep, setRegStep] = useState(0); // 0=system test, 1=face, 2=ID
  const [toast, setToast] = useState({ open: false, text: '', title: '' });
  const lastViolationToastRef = useRef({ type: '', at: 0 });
  const lastLoudSoundViolationAtRef = useRef(0);
  const pendingViolationPostsRef = useRef([]);
  const TOAST_VIOLATION_COOLDOWN_MS = 15000;
  const [faceUploaded, setFaceUploaded] = useState(false);
  const [idUploaded, setIdUploaded] = useState(false);
  const [faceUploading, setFaceUploading] = useState(false);
  const [idUploading, setIdUploading] = useState(false);
  const [completingRegistration, setCompletingRegistration] = useState(false);
  const [previewStreamReady, setPreviewStreamReady] = useState(false);
  const [regError, setRegError] = useState('');
  const [sysRunning, setSysRunning] = useState(false);
  const [hasRunSystemTest, setHasRunSystemTest] = useState(false);
  const [sysResults, setSysResults] = useState({ camera: false, models: false, speech: false, backend: false, faceDetected: false, faceCount: 0 });
  const [examActiveInternal, setExamActiveInternal] = useState(false);
  const [webcamRequested, setWebcamRequested] = useState(false);
  const [webcamFeedExamId, setWebcamFeedExamId] = useState(null); // examId from start_webcam_feed so frames go to admin's room
  const [violationsPaused, setViolationsPaused] = useState(false);
  const violationsPausedRef = useRef(false);
  const [noFaceModalOpen, setNoFaceModalOpen] = useState(false);
  const lastFaceSeenAtRef = useRef(Date.now());
  const [verifyStats, setVerifyStats] = useState({ ok: 0, fail: 0 });
  const faceVerifyRunCountRef = useRef(0); // skip first run for admin (first often fails)
  const faceVerifyConsecutiveFailuresRef = useRef(0); // only report violation after 2+ consecutive failures
  const [prerequisitesComplete, setPrerequisitesComplete] = useState(false);
  const [userClickedStartMonitoring, setUserClickedStartMonitoring] = useState(false);
  const [capturedViolations, setCapturedViolations] = useState([]); // debug: all violations that passed through reportViolation
  const [sensorDebug, setSensorDebug] = useState({ brightness: null, noiseLevel: null, noiseMonitoring: false, noiseError: null, audioContextState: null, screenCapture: false, screenCaptureReason: null });
  const hostRef = useRef(null);
  const [hostZIndex, setHostZIndex] = useState(null);
  const registrationCheckKeyRef = useRef(null);
  const onRegistrationStatusRef = useRef(onRegistrationStatus);
  const onReadyForMonitoringRef = useRef(onReadyForMonitoring);
  onRegistrationStatusRef.current = onRegistrationStatus;
  onReadyForMonitoringRef.current = onReadyForMonitoring;

  const environmentCheckDismissedRef = useRef(false);
  const environmentCheckCompletedRef = useRef(false);
  const serverTimeOffsetRef = useRef(0);
  const [serverTimeSynced, setServerTimeSynced] = useState(false);
  // Forces re-render when schedule boundaries are crossed (T-N open, exam start)
  const [scheduleTick, setScheduleTick] = useState(0);

  useEffect(() => {
    environmentCheckDismissedRef.current = environmentCheckDismissed;
  }, [environmentCheckDismissed]);

  useEffect(() => {
    environmentCheckCompletedRef.current = environmentCheckCompleted;
  }, [environmentCheckCompleted]);

  // Server-adjusted clock for exam schedule (examStartAt is server-authored).
  const getNowMs = React.useCallback(
    () => Date.now() + serverTimeOffsetRef.current,
    []
  );

  useEffect(() => {
    violationsPausedRef.current = violationsPaused;
  }, [violationsPaused]);
  const resolvedApiBaseUrl = resolveApiBaseUrl(apiBaseUrl);
  const wsUrl = resolveWebsocketUrl(websocketUrl, resolvedApiBaseUrl);
  const examRoomId = (adminExamId && String(adminExamId)) || (libraryId && String(libraryId)) || String(examId);
  const resolvedIacId =
    iacId != null && iacId !== '' && Number.isFinite(Number(iacId)) ? Number(iacId) : null;

  // Align exam scheduling with server clock (not the student's PC clock)
  useEffect(() => {
    if (!resolvedApiBaseUrl) return undefined;
    let cancelled = false;

    async function syncServerTime() {
      const t0 = Date.now();
      try {
        const res = await apiFetch(resolvedApiBaseUrl, '/api/ping');
        const data = await res.json();
        if (cancelled || !data || !data.success) return;
        const t1 = Date.now();
        const serverTimeMs = typeof data.serverTimeMs === 'number'
          ? data.serverTimeMs
          : typeof data.serverTime === 'number'
            ? data.serverTime
            : null;
        if (serverTimeMs == null) return;
        const rttHalf = (t1 - t0) / 2;
        const estimatedServerNow = serverTimeMs + rttHalf;
        const offsetMs = estimatedServerNow - Date.now();
        serverTimeOffsetRef.current = offsetMs;
        if (!cancelled) setServerTimeSynced(true);
        // eslint-disable-next-line no-console
        console.log('[ProctoringWidget][Schedule] server time sync', {
          offsetMs: Math.round(offsetMs),
          clientNowIso: new Date().toISOString(),
          serverNowIso: new Date(Date.now() + offsetMs).toISOString(),
          rttMs: Math.round(t1 - t0),
        });
      } catch (_) { /* ignore — fall back to client clock */ }
    }

    syncServerTime();
    const poll = setInterval(syncServerTime, 60000);
    return () => {
      cancelled = true;
      clearInterval(poll);
    };
  }, [resolvedApiBaseUrl]);

  const scheduleNowMs = conservativeNowMs(getNowMs(), Date.now());
  const resolvedSystemCheckMinutes = normalizeSystemCheckMinutesBefore(systemCheckMinutesBeforeExam);
  const examStartTs = parseExamTimestamp(examStartAt);
  const examHasEnded = !!endExam;
  // Strict T-N gate — false before the window; never open env check early
  const inEnvironmentCheckPeriod = !examHasEnded
    && examStartTs != null
    && isEnvironmentCheckPeriod(
      examStartAt,
      endExam,
      resolvedSystemCheckMinutes,
      scheduleNowMs
    );
  const msUntilEnvCheckWindow = examStartTs == null
    ? null
    : msUntilSystemCheckWindow(examStartAt, resolvedSystemCheckMinutes, scheduleNowMs);
  // From T-N (env check window open) through exam until endExam — admin presence window
  const proctoringSessionOpen = isProctoringSessionPeriod(
    examStartAt,
    endExam,
    resolvedSystemCheckMinutes,
    scheduleNowMs
  );
  const examHasStartedByTime = hasExamStarted(examStartAt, scheduleNowMs);
  // Fail closed: missing/unparseable examStartAt means we are not waiting AND not live
  const examStartMissing = examStartTs == null;
  const examWaitingToStart = examStartMissing || examHasStartedByTime === false;
  // Explicit examIsActive prop wins. If omitted, become active at examStartAt (schedule-driven)
  // or when WS requests a webcam feed — do not stay stuck on examActiveInternal=false forever.
  const isExamActiveProp = typeof examIsActive === 'boolean'
    ? !!examIsActive
    : (!!examActiveInternal || examHasStartedByTime === true);
  // Exam is live only after a valid examStartAt has been reached (and optional examIsActive gate)
  const isExamLive = !examHasEnded && !examWaitingToStart && isExamActiveProp;
  const isExamActive = isExamLive;
  const monitoringArmed = userClickedStartMonitoring || !!start;
  const systemCheckRequired = requireSystemCheck !== false;
  const envCheckSatisfied = !systemCheckRequired || environmentCheckCompleted;
  const canRunMonitoring = !isExcludedFromMonitoring
    && isRegistered
    && prerequisitesComplete
    && monitoringArmed
    && isExamLive
    && envCheckSatisfied;
  const monitoringStarted = canRunMonitoring;
  // After env-check, keep camera warm until exam monitoring starts (otherwise tracks are stopped
  // and often fail to restart cleanly when examStartAt arrives).
  const keepCameraForArmedSession = environmentCheckCompleted
    && monitoringArmed
    && !examHasEnded
    && !!isRegistered
    && proctoringSessionOpen;
  // Presence: connect as soon as we have exam/user ids so admin sees Connected.
  // Env-check / monitoring stay gated separately by T-N and registration.
  const wsSessionActive = !isExcludedFromMonitoring && !!userId && !!examRoomId && !examHasEnded;

  // Full-screen client mount overlays sit above the exam. Force pointer-events:none on them
  // for the whole widget lifetime so the exam stays clickable while env-check / monitoring
  // panels (portaled to body with pointer-events:auto) remain usable.
  useEffect(() => {
    const host = hostRef.current;
    if (!host || typeof document === 'undefined') return undefined;
    setHostZIndex(resolveHostZIndex(host));

    const applied = [];
    const apply = () => {
      setHostZIndex(resolveHostZIndex(host));
      const overlays = collectClickThroughOverlays(host);
      overlays.forEach((el) => {
        let entry = applied.find((a) => a.el === el);
        if (!entry) {
          entry = {
            el,
            prev: el.style.getPropertyValue('pointer-events'),
            prevPriority: el.style.getPropertyPriority('pointer-events'),
          };
          applied.push(entry);
        }
        // Re-force each time — client CSS/animations may reset pointer-events.
        el.style.setProperty('pointer-events', 'none', 'important');
      });
      // eslint-disable-next-line no-console
      console.log('[ProctoringWidget][UI] click-through overlays', {
        count: applied.length,
        hostZIndex: resolveHostZIndex(host),
      });
    };

    apply();
    // Re-apply after layout / client late styles, and keep enforcing while mounted.
    const t1 = setTimeout(apply, 0);
    const t2 = setTimeout(apply, 250);
    const poll = setInterval(apply, 2000);
    const onResize = () => apply();
    window.addEventListener('resize', onResize);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearInterval(poll);
      window.removeEventListener('resize', onResize);
      applied.forEach(({ el, prev, prevPriority }) => {
        try {
          if (prev) el.style.setProperty('pointer-events', prev, prevPriority || undefined);
          else el.style.removeProperty('pointer-events');
        } catch (_) { /* ignore */ }
      });
    };
  }, [
    systemCheckOpenForRegistered,
    registrationOpen,
    canRunMonitoring,
    isRegistered,
  ]);

  const stopAllVirgilActivity = React.useCallback(() => {
    setIsExcludedFromMonitoring(true);
    setRegistrationOpen(false);
    setSystemCheckOpenForRegistered(false);
    setUserClickedStartMonitoring(false);
    setWebcamRequested(false);
    setWebcamFeedExamId(null);
    setViolationsPaused(true);
    onReadyForMonitoringRef.current && onReadyForMonitoringRef.current(false);
    if (mediaStreamRef.current) {
      try { mediaStreamRef.current.getTracks().forEach((t) => t.stop()); } catch (_) { /* ignore */ }
      mediaStreamRef.current = null;
    }
    if (regStreamRef.current) {
      try { regStreamRef.current.getTracks().forEach((t) => t.stop()); } catch (_) { /* ignore */ }
      regStreamRef.current = null;
    }
    setIsStreaming(false);
  }, []);

  // Log exam schedule props whenever they change
  useEffect(() => {
    console.log('[ProctoringWidget][Schedule] exam timing received', {
      userId,
      examId,
      isRegistered,
      endExam,
      examIsActive: typeof examIsActive === 'boolean' ? examIsActive : '(not set — using WS/internal)',
      systemCheckMinutesBeforeExam: resolvedSystemCheckMinutes,
      systemCheckMinutesBeforeExamRaw: systemCheckMinutesBeforeExam,
      serverTimeSynced,
      serverTimeOffsetMs: Math.round(serverTimeOffsetRef.current),
      scheduleNowMs,
      inEnvironmentCheckPeriod,
      msUntilEnvCheckWindow,
      ...describeExamSchedule(examStartAt, endExam, resolvedSystemCheckMinutes, scheduleNowMs),
    });
    if (endExam) {
      // eslint-disable-next-line no-console
      console.log('[ProctoringWidget][Schedule] endExam=true — monitoring stopped');
    }
    if (examStartAt == null || examStartAt === '') {
      // eslint-disable-next-line no-console
      console.warn('[ProctoringWidget][Schedule] examStartAt is missing — environment check cannot be scheduled. Pass examStartAt prop from the client.');
    } else if (parseExamTimestamp(examStartAt) == null) {
      // eslint-disable-next-line no-console
      console.warn('[ProctoringWidget][Schedule] examStartAt could not be parsed:', examStartAt);
    } else {
      const schedule = describeExamSchedule(examStartAt, endExam, resolvedSystemCheckMinutes, scheduleNowMs);
      // eslint-disable-next-line no-console
      console.log('[ProctoringWidget][Schedule] examStartAt parsed OK:', {
        raw: examStartAt,
        iso: schedule.parsed.examStartAtIso,
        examHasStarted: schedule.window.examHasStarted,
        minutesUntilExamStart: schedule.window.minutesUntilExamStart,
        minutesUntilEnvCheck: schedule.window.minutesUntilWindow,
        inEnvironmentCheckPeriod: schedule.window.inEnvironmentCheckPeriod,
        proctoringSessionOpen: schedule.window.proctoringSessionOpen,
      });
    }
  }, [userId, examId, examStartAt, endExam, resolvedSystemCheckMinutes, systemCheckMinutesBeforeExam, isRegistered, examIsActive, serverTimeSynced, scheduleNowMs, inEnvironmentCheckPeriod, msUntilEnvCheckWindow, scheduleTick]);

  // Log monitoring gate — why monitoring is or isn't running
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('[ProctoringWidget][Monitoring] gate status', {
      canRunMonitoring,
      wsSessionActive,
      proctoringSessionOpen,
      isRegistered,
      prerequisitesComplete,
      monitoringArmed,
      startProp: !!start,
      userClickedStartMonitoring,
      examIsActiveProp: isExamActiveProp,
      examWaitingToStart,
      examStartMissing,
      examHasStartedByTime,
      isExamLive,
      endExam,
      inEnvironmentCheckPeriod,
      environmentCheckCompleted,
      envCheckSatisfied,
      systemCheckRequired,
      examStartAtRaw: examStartAt,
      examStartAtIso: formatExamTimestamp(examStartAt),
    });
    if (monitoringArmed && !canRunMonitoring) {
      const reasons = [];
      if (!isRegistered) reasons.push('not registered');
      if (!prerequisitesComplete) reasons.push('prerequisites incomplete');
      if (examStartMissing) reasons.push('examStartAt missing or unparseable (fail closed)');
      else if (examWaitingToStart) reasons.push('exam has not started yet (waiting for examStartAt)');
      if (!isExamActiveProp) reasons.push('examIsActive is false (pass examIsActive or wait for examStartAt)');
      if (examHasEnded) reasons.push('endExam is true');
      if (!envCheckSatisfied) reasons.push('environment check not completed');
      // eslint-disable-next-line no-console
      console.log('[ProctoringWidget][Monitoring] armed but not running because:', reasons.join(', ') || 'unknown');
    }
    if (canRunMonitoring) {
      // eslint-disable-next-line no-console
      console.log('[ProctoringWidget][Monitoring] ACTIVE — violation monitoring running');
    }
  }, [
    canRunMonitoring,
    wsSessionActive,
    proctoringSessionOpen,
    isRegistered,
    prerequisitesComplete,
    monitoringArmed,
    start,
    userClickedStartMonitoring,
    isExamActiveProp,
    examWaitingToStart,
    examStartMissing,
    examHasStartedByTime,
    isExamLive,
    endExam,
    inEnvironmentCheckPeriod,
    environmentCheckCompleted,
    envCheckSatisfied,
    systemCheckRequired,
    examStartAt,
  ]);

  // Tick schedule on window open / exam start so WS and env-check update without needing a modal state change
  useEffect(() => {
    if (!examStartAt || examHasEnded) return undefined;
    if (parseExamTimestamp(examStartAt) == null) return undefined;

    const tick = () => setScheduleTick((n) => n + 1);

    const msUntilWindow = msUntilSystemCheckWindow(examStartAt, resolvedSystemCheckMinutes, getNowMs());
    const startTs = parseExamTimestamp(examStartAt);
    const now = getNowMs();
    const timers = [];

    if (msUntilWindow != null && msUntilWindow > 0) {
      timers.push(setTimeout(tick, Math.min(msUntilWindow + 50, 2147483647)));
    }
    if (startTs != null && startTs > now) {
      timers.push(setTimeout(tick, Math.min(startTs - now + 50, 2147483647)));
    }

    const getPollMs = () => {
      const until = msUntilSystemCheckWindow(examStartAt, resolvedSystemCheckMinutes, getNowMs());
      // Before T-N: poll slowly. Near/inside window: poll more often for a crisp open.
      if (until != null && until > 15 * 60 * 1000) return 30000;
      if (until != null && until > 0) return 5000;
      if (isProctoringSessionPeriod(examStartAt, endExam, resolvedSystemCheckMinutes, getNowMs())) return 15000;
      return 30000;
    };

    let pollId;
    const schedulePoll = () => {
      pollId = setTimeout(() => {
        tick();
        schedulePoll();
      }, getPollMs());
    };
    schedulePoll();

    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(pollId);
    };
  }, [examStartAt, endExam, resolvedSystemCheckMinutes, examHasEnded, getNowMs, serverTimeSynced]);

  // Log when environment check window opens or closes
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('[ProctoringWidget][Schedule] inEnvironmentCheckPeriod =', inEnvironmentCheckPeriod, {
      systemCheckOpen: systemCheckOpenForRegistered,
      environmentCheckDismissed,
      examHasEnded,
      proctoringSessionOpen,
      wsSessionActive,
    });
  }, [inEnvironmentCheckPeriod, systemCheckOpenForRegistered, environmentCheckDismissed, examHasEnded, proctoringSessionOpen, wsSessionActive]);

  async function captureCurrentFrame() {
    // Prefer main stream when active; otherwise fall back to preview (setup)
    const main = videoRef.current;
    const preview = previewVideoRef.current;
    const source = (main && isStreaming && main.videoWidth > 0) ? main : (preview && preview.videoWidth > 0 ? preview : null);
    if (!source) return null;
    const canvas = document.createElement('canvas');
    canvas.width = source.videoWidth; canvas.height = source.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(source, 0, 0);
    return canvas.toDataURL('image/jpeg');
  }

  async function uploadSnapshotToS3(dataUrl) {
    try {
      if (!dataUrl) return null;
      const res = await apiFetch(resolvedApiBaseUrl, '/api/upload-snapshot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, examId, image: dataUrl })
      });
      const data = await res.json();
      if (data && data.success && data.url) return data.url;
    } catch (_) {}
    return null;
  }

  async function postViolationToServer(v) {
    if (!resolvedApiBaseUrl) return;
    try {
      const res = await apiFetch(resolvedApiBaseUrl, '/api/record-violation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(v)
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        // eslint-disable-next-line no-console
        console.warn('[ProctoringWidget] record-violation HTTP error', res.status, text.slice(0, 200));
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[ProctoringWidget] record-violation network error', err && (err.message || String(err)));
      if (sendViolationsToServer && isExamActive) {
        pendingViolationPostsRef.current.push(v);
      }
    }
  }

  async function reportViolation({ type, details, severity }) {
    if (violationsPaused) return; // pause all violation records when paused
    if (type === 'Loud Sound Detected') {
      const now = Date.now();
      if (now - lastLoudSoundViolationAtRef.current < NOISE_VIOLATION_COOLDOWN_MS) return;
      lastLoudSoundViolationAtRef.current = now;
    }
    let snapshotUrl = undefined;
    if (snapshotOnViolation) {
      try {
        const frame = await captureCurrentFrame();
        snapshotUrl = await uploadSnapshotToS3(frame) || undefined;
      } catch (_) {}
    }
    const clientViolationId = createClientViolationId();
    const violation = {
      type,
      details,
      severity,
      userId,
      examId: examRoomId,
      timestamp: new Date().toISOString(),
      snapshot_url: snapshotUrl,
      clientViolationId,
      ...(resolvedIacId != null ? { iacId: resolvedIacId } : {})
    };
    // eslint-disable-next-line no-console
    console.log('[Proctoring][Widget][violation]', { clientViolationId, type, examId: examRoomId });
    const attemptedSendToServer = !!(sendViolationsToServer && isExamActive);
    setCapturedViolations(prev => [...prev.slice(-99), { ...violation, _attemptedSendToServer: attemptedSendToServer, _attemptedSendToWs: true }]);
    onViolation && onViolation(violation);
    if (sendViolationsToServer && isExamActive) {
      postViolationToServer(violation);
    }
    // Send via WebSocket for real-time admin delivery (queues if offline)
    try {
      ws.send('violation', { ...violation, examId: examRoomId });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[ProctoringWidget] ws.send violation failed', err && (err.message || String(err)));
    }
    // Show violation to user (toast) with cooldown per type to avoid spam
    const now = Date.now();
    const last = lastViolationToastRef.current;
    if (last.type !== type || now - last.at > TOAST_VIOLATION_COOLDOWN_MS) {
      lastViolationToastRef.current = { type, at: now };
      setToast({ open: true, text: details || type, title: 'Proctoring alert' });
    }
  }

  // Connect WS when environment check window opens (pre-exam); keep alive until exam ends
  useEffect(() => {
    if (!wsSessionActive) return undefined;
    // eslint-disable-next-line no-console
    console.log('[ProctoringWidget] Init WS connect as user', {
      examId: String(examRoomId),
      userId: String(userId),
      url: String(wsUrl),
      proctoringSessionOpen,
    });
    ws.connect({ url: wsUrl, examId: examRoomId, userId: String(userId), role: 'student' });
    return () => {
      try { ws.disconnect(); } catch (_) {}
    };
  }, [wsSessionActive, userId, examRoomId, wsUrl]);

  // WS event handlers — separate from connect so phase changes do not tear down the socket
  useEffect(() => {
    if (!wsSessionActive) return undefined;
    const offExcluded = ws.on('monitoring_excluded', (payload) => {
      if (!payload) return;
      if (payload.userId != null && String(payload.userId) !== String(userId)) return;
      // eslint-disable-next-line no-console
      console.log('[ProctoringWidget] monitoring_excluded received — stopping all Virgil activity');
      stopAllVirgilActivity();
    });
    const offIncluded = ws.on('monitoring_included', (payload) => {
      if (!payload) return;
      if (payload.userId != null && String(payload.userId) !== String(userId)) return;
      // eslint-disable-next-line no-console
      console.log('[ProctoringWidget] monitoring_included received — user may re-register');
      setIsExcludedFromMonitoring(false);
      setViolationsPaused(false);
      setPrerequisitesComplete(false);
      setIsRegistered(false);
      if (!registrationDismissed) {
        setRegistrationOpen(true);
        setRegStep(0);
      }
    });
    const offAdminMsg = ws.on('admin_message', (payload) => {
      if (!payload || !payload.message) return;
      // Show when payload is for this user, or when server doesn't send userId (treat as for me)
      if (payload.userId != null && String(payload.userId) !== String(userId)) return;
      setToast({ open: true, text: String(payload.message), title: 'Message from Admin' });
      // eslint-disable-next-line no-console
      console.log('[ProctoringWidget] admin_message', payload);
    });
    // Catch-all debug to verify event flow
    const offAny = ws.on('*', (evt, arg) => {
      if (evt === 'admin_message') {
        // eslint-disable-next-line no-console
        console.log('[ProctoringWidget] onAny admin_message', arg);
      }
    });
    const offStart = ws.on('start_webcam_feed', (payload) => {
      if (!payload) return;
      const userMatch = String(payload.userId) === String(userId);
      if (userMatch) {
        setWebcamRequested(true);
        setWebcamFeedExamId(payload.examId != null ? String(payload.examId) : null);
        if (typeof examIsActive !== 'boolean') setExamActiveInternal(true);
        // eslint-disable-next-line no-console
        console.log('[ProctoringWidget] start_webcam_feed received for user, enabling frames', { examId: payload.examId });
      }
    });
    const offStop = ws.on('stop_webcam_feed', (payload) => {
      if (!payload) return;
      const userMatch = String(payload.userId) === String(userId);
      if (userMatch) {
        setWebcamRequested(false);
        setWebcamFeedExamId(null);
        if (typeof examIsActive !== 'boolean') setExamActiveInternal(false);
        // eslint-disable-next-line no-console
        console.log('[ProctoringWidget] stop_webcam_feed received for user, disabling frames');
      }
    });
    return () => { try { offStart?.(); offStop?.(); offAdminMsg?.(); offAny?.(); offExcluded?.(); offIncluded?.(); } catch (_) {} };
  }, [wsSessionActive, userId, examId, examRoomId, examIsActive, stopAllVirgilActivity, registrationDismissed]);

  // Log when parent toggles exam
  useEffect(() => {
    if (typeof examIsActive === 'boolean') {
      // eslint-disable-next-line no-console
      console.log('[ProctoringWidget] Exam active (prop override):', !!examIsActive);
    }
  }, [examIsActive]);

  // Retry POST /api/record-violation after browser comes back online
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const flush = async () => {
      if (!resolvedApiBaseUrl) return;
      const batch = pendingViolationPostsRef.current.splice(0);
      if (batch.length === 0) return;
      // eslint-disable-next-line no-console
      console.log('[ProctoringWidget] Flushing pending violation POSTs', batch.length);
      for (const v of batch) {
        try {
          const res = await apiFetch(resolvedApiBaseUrl, '/api/record-violation', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(v)
          });
          if (!res.ok) {
            pendingViolationPostsRef.current.push(v);
            // eslint-disable-next-line no-console
            console.warn('[ProctoringWidget] Pending violation POST still failed after online', res.status);
          }
        } catch (err) {
          pendingViolationPostsRef.current.push(v);
          // eslint-disable-next-line no-console
          console.warn('[ProctoringWidget] Pending violation POST error after online', err && (err.message || String(err)));
        }
      }
    };
    window.addEventListener('online', flush);
    return () => window.removeEventListener('online', flush);
  }, [resolvedApiBaseUrl]);

  // Registration check (uses check-registration with userId+examId - matches user_registrations table)
  // Only re-run when the identity of the check changes — parent callbacks must not retrigger this POST.
  useEffect(() => {
    if (!userId || !examId) return;
    const checkKey = `${userId}:${examId}`;
    const isNewSession = registrationCheckKeyRef.current !== checkKey;
    registrationCheckKeyRef.current = checkKey;
    if (isNewSession) {
      setIsRegistered(false);
      setPrerequisitesComplete(false);
      setUserClickedStartMonitoring(false);
      setEnvironmentCheckCompleted(false);
      setEnvironmentCheckDismissed(false);
      setSystemCheckOpenForRegistered(false);
      setIsExcludedFromMonitoring(false);
      onReadyForMonitoringRef.current && onReadyForMonitoringRef.current(false);
    }
    let alive = true;
    (async () => {
      try {
        const res = await apiFetch(resolvedApiBaseUrl, '/api/check-registration', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId, examId })
        });
        const data = await res.json();
        if (!alive) return;
        if (data.excluded) {
          stopAllVirgilActivity();
          onRegistrationStatusRef.current && onRegistrationStatusRef.current(false);
          return;
        }
        const exists = !!data.registered;
        onRegistrationStatusRef.current && onRegistrationStatusRef.current(exists);
        setIsRegistered(exists);
        if (exists) {
          setPrerequisitesComplete(true);
          if (data.environmentCheckComplete) {
            setEnvironmentCheckCompleted(true);
            setUserClickedStartMonitoring(true);
            onReadyForMonitoringRef.current && onReadyForMonitoringRef.current(true);
            // eslint-disable-next-line no-console
            console.log('[ProctoringWidget][Schedule] environment check already complete (audit)', {
              completedAt: data.environmentCheckCompletedAt,
            });
          }
        } else if (!registrationDismissed) {
          setRegistrationOpen(true);
          const envOpen = isEnvironmentCheckPeriod(
            examStartAt,
            endExam,
            resolvedSystemCheckMinutes,
            conservativeNowMs(getNowMs(), Date.now())
          );
          setRegStep(envOpen ? 0 : 1);
        }
      } catch (_) {
        if (!alive) return;
        onRegistrationStatusRef.current && onRegistrationStatusRef.current(false);
        setIsRegistered(false);
        if (!registrationDismissed) {
          setRegistrationOpen(true);
          const envOpen = isEnvironmentCheckPeriod(
            examStartAt,
            endExam,
            resolvedSystemCheckMinutes,
            conservativeNowMs(getNowMs(), Date.now())
          );
          setRegStep(envOpen ? 0 : 1);
        }
      }
    })();
    return () => { alive = false; };
  }, [userId, examId, resolvedApiBaseUrl, stopAllVirgilActivity]);

  // Open environment check only once T-N begins (never before). Stay available through exam until completed/endExam.
  useEffect(() => {
    if (!isRegistered || !examStartAt || examHasEnded) {
      setSystemCheckOpenForRegistered(false);
      return undefined;
    }
    if (parseExamTimestamp(examStartAt) == null) {
      setSystemCheckOpenForRegistered(false);
      return undefined;
    }

    const syncEnvironmentCheck = () => {
      const now = conservativeNowMs(getNowMs(), Date.now());
      const schedule = describeExamSchedule(examStartAt, endExam, resolvedSystemCheckMinutes, now);
      const msUntil = schedule.window.msUntilWindow;
      // Hard rule: before T-N the modal must stay closed
      const inWindow = isEnvironmentCheckPeriod(examStartAt, endExam, resolvedSystemCheckMinutes, now);
      const dismissed = environmentCheckDismissedRef.current;
      const completed = environmentCheckCompletedRef.current;
      // eslint-disable-next-line no-console
      console.log('[ProctoringWidget][Schedule] environment check sync', {
        inWindow,
        willOpen: !!(inWindow && !dismissed && !completed && !examHasEnded),
        msUntilWindow: msUntil,
        minutesUntilWindow: msUntil != null ? Math.round(msUntil / 60000) : null,
        examStartAtRaw: examStartAt,
        examStartAtIso: schedule.parsed.examStartAtIso,
        nowIso: new Date(now).toISOString(),
        environmentCheckDismissed: dismissed,
        environmentCheckCompleted: completed,
        ...schedule.window,
      });
      if (!inWindow || dismissed || completed || examHasEnded) {
        setSystemCheckOpenForRegistered(false);
        return;
      }
      setSystemCheckOpenForRegistered((open) => {
        if (!open) {
          // eslint-disable-next-line no-console
          console.log('[ProctoringWidget][Schedule] opening environment check — T-' + resolvedSystemCheckMinutes + ' window started');
        }
        return true;
      });
    };

    syncEnvironmentCheck();

    const delay = msUntilSystemCheckWindow(examStartAt, resolvedSystemCheckMinutes, getNowMs());
    let openTimer;
    // Arm a timer for the exact T-N boundary.
    if (delay != null && delay > 0) {
      openTimer = setTimeout(syncEnvironmentCheck, Math.min(delay + 100, 2147483647));
    }

    // Near the window, poll often so we don't miss the open edge.
    const getPollIntervalMs = () => {
      const msUntil = msUntilSystemCheckWindow(examStartAt, resolvedSystemCheckMinutes, getNowMs());
      if (msUntil != null && msUntil > 60 * 1000) return 15000;
      if (msUntil != null && msUntil > 0) return 2000;
      return 10000;
    };

    let pollTimer;
    const schedulePoll = () => {
      pollTimer = setTimeout(() => {
        syncEnvironmentCheck();
        schedulePoll();
      }, getPollIntervalMs());
    };
    schedulePoll();

    return () => {
      if (openTimer) clearTimeout(openTimer);
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [
    isRegistered,
    examStartAt,
    endExam,
    examHasEnded,
    environmentCheckDismissed,
    environmentCheckCompleted,
    resolvedSystemCheckMinutes,
    getNowMs,
    serverTimeSynced,
    scheduleTick,
  ]);

  // Stop monitoring when client sets endExam=true
  useEffect(() => {
    if (!examHasEnded) return;
    // eslint-disable-next-line no-console
    console.log('[ProctoringWidget][Schedule] endExam=true — stopping monitoring and closing panels');
    setSystemCheckOpenForRegistered(false);
    setUserClickedStartMonitoring(false);
    setRegSuccessOpen(false);
    setNoFaceModalOpen(false);
    setRegistrationOpen(false);
  }, [examHasEnded]);

  // System check (minimal: immediately pass when requested)
  useEffect(() => {
    if (runSystemCheck) {
      onSystemCheckComplete && onSystemCheckComplete(true);
    }
  }, [runSystemCheck, onSystemCheckComplete]);

  // Webcam start/stop: registration, env-check, armed wait for exam, or active monitoring
  useEffect(() => {
    let cancelled = false;
    async function startCam() {
      // Portal video may mount one frame late after shouldMountVideo flips true
      let el = videoRef.current;
      if (!el) {
        await new Promise((r) => requestAnimationFrame(() => r()));
        el = videoRef.current;
      }
      if (!el || cancelled) return;
      // Already streaming onto this element — keep existing tracks (avoid stop/restart gap)
      if (
        mediaStreamRef.current
        && mediaStreamRef.current.active
        && el.srcObject === mediaStreamRef.current
      ) {
        setIsStreaming(true);
        return;
      }
      try {
        const wantAudio = enableNoiseMonitoring && (canRunMonitoring || monitoringStarted);
        const videoConstraints = { width: 640, height: 480, facingMode: 'user' };
        const audioConstraints = { echoCancellation: true, noiseSuppression: true, autoGainControl: true };
        let stream;
        if (wantAudio) {
          try {
            stream = await navigator.mediaDevices.getUserMedia({ video: videoConstraints, audio: audioConstraints });
          } catch (_) {
            stream = await navigator.mediaDevices.getUserMedia({ video: videoConstraints, audio: false });
          }
        } else {
          stream = await navigator.mediaDevices.getUserMedia({ video: videoConstraints, audio: false });
        }
        if (cancelled) {
          try { stream.getTracks().forEach((t) => t.stop()); } catch (_) { /* ignore */ }
          return;
        }
        // Stop previous tracks only after we have a replacement stream
        if (mediaStreamRef.current && mediaStreamRef.current !== stream) {
          try { mediaStreamRef.current.getTracks().forEach((t) => t.stop()); } catch (_) { /* ignore */ }
        }
        mediaStreamRef.current = stream;
        el = videoRef.current || el;
        el.srcObject = stream;
        el.muted = true;
        await el.play().catch(() => {});
        if (!cancelled) setIsStreaming(true);
      } catch (_) {
        if (!cancelled) setIsStreaming(false);
      }
    }
    function stopCam() {
      if (mediaStreamRef.current) {
        try { mediaStreamRef.current.getTracks().forEach((t) => t.stop()); } catch (_) { /* ignore */ }
        mediaStreamRef.current = null;
      }
      setIsStreaming(false);
    }
    const needsCam = !isExcludedFromMonitoring && (
      registrationOpen
      || (systemCheckOpenForRegistered && inEnvironmentCheckPeriod)
      || keepCameraForArmedSession
      || canRunMonitoring
      || webcamRequested
    );
    if (needsCam) startCam();
    else stopCam();
    // Do not stop tracks in cleanup when needsCam stays true across phase changes
    // (env-check → armed wait → monitoring). A separate unmount effect releases the camera.
    return () => { cancelled = true; };
  }, [
    canRunMonitoring,
    monitoringStarted,
    registrationOpen,
    systemCheckOpenForRegistered,
    inEnvironmentCheckPeriod,
    keepCameraForArmedSession,
    webcamRequested,
    enableNoiseMonitoring,
    isExcludedFromMonitoring,
  ]);

  // Ensure getUserMedia tracks are released when the widget unmounts
  useEffect(() => () => {
    if (mediaStreamRef.current) {
      try { mediaStreamRef.current.getTracks().forEach((t) => t.stop()); } catch (_) { /* ignore */ }
      mediaStreamRef.current = null;
    }
  }, []);

  // Start camera stream for registration modal: system check (step 0) and face capture (step 1)
  // Always start our own stream when in registration - main video is hidden so we can't use it
  useEffect(() => {
    let cancelled = false;
    async function ensurePreviewStream() {
      if (!registrationOpen || (regStep !== 0 && regStep !== 1)) return;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 480, facingMode: 'user' }, audio: false });
        if (cancelled) { try { stream.getTracks().forEach(t => t.stop()); } catch (_) {} return; }
        regStreamRef.current = stream;
        // Small delay so ref is attached after step transition (step 0 <-> 1)
        await new Promise(r => setTimeout(r, 0));
        if (cancelled) return;
        const el = previewVideoRef.current;
        if (el) {
          try {
            el.setAttribute('playsinline', 'true');
            el.muted = true;
            el.srcObject = stream;
            await el.play().catch(() => {});
            if (!cancelled) setPreviewStreamReady(true);
          } catch (_) {}
        }
      } catch (_) {}
    }
    setPreviewStreamReady(false);
    ensurePreviewStream();
    return () => {
      cancelled = true;
      setPreviewStreamReady(false);
      if (regStreamRef.current) {
        try { regStreamRef.current.getTracks().forEach(t => t.stop()); } catch (_) {}
        regStreamRef.current = null;
      }
    };
  }, [registrationOpen, regStep]);

  async function enablePreviewStream() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 480, facingMode: 'user' }, audio: false });
      regStreamRef.current = stream;
      if (previewVideoRef.current) {
        try {
          previewVideoRef.current.setAttribute('playsinline', 'true');
          previewVideoRef.current.muted = true;
          previewVideoRef.current.srcObject = stream;
          await previewVideoRef.current.play().catch(() => {});
          setPreviewStreamReady(true);
        } catch (_) {}
      }
    } catch (_) {}
  }

  // Mirror main stream to preview when available (registration face step or environment check)
  useEffect(() => {
    const v = previewVideoRef.current;
    if (!v) return;
    const wantsPreview = (registrationOpen && regStep === 1) || systemCheckOpenForRegistered;
    if (!wantsPreview) return;
    if (isStreaming && mediaStreamRef.current) {
      try {
        v.srcObject = mediaStreamRef.current;
        v.play().catch(() => {});
      } catch (_) {}
    }
  }, [registrationOpen, regStep, systemCheckOpenForRegistered, isStreaming]);

  // Load face-api model once (configurable base URL) with diagnostics
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const base = modelsBaseUrl || '/models';
      setModelDiag({ base, startedAt: Date.now(), endedAt: 0, success: false, error: '', manifestStatus: null });
      try {
        // eslint-disable-next-line no-console
        console.log('[ProctoringWidget] Loading TinyFaceDetector from', base);
        await faceapi.nets.tinyFaceDetector.loadFromUri(base);
        if (!cancelled) {
          setModelsLoaded(true);
          setModelDiag(prev => ({ ...prev, endedAt: Date.now(), success: true }));
          // eslint-disable-next-line no-console
          console.log('[ProctoringWidget] TinyFaceDetector loaded successfully');
        }
      } catch (err) {
        if (!cancelled) {
          setModelsLoaded(false);
          const message = (err && (err.message || String(err))) || 'Unknown error';
          setModelDiag(prev => ({ ...prev, endedAt: Date.now(), success: false, error: message }));
          // eslint-disable-next-line no-console
          console.error('[ProctoringWidget] TinyFaceDetector load failed:', message);
          try {
            const manifestUrl = `${base.replace(/\/$/, '')}/tiny_face_detector_model-weights_manifest.json`;
            const resp = await fetch(manifestUrl, { cache: 'no-store' });
            // eslint-disable-next-line no-console
            console.log('[ProctoringWidget] Manifest probe', manifestUrl, 'status =', resp.status);
            setModelDiag(prev => ({ ...prev, manifestStatus: resp.status }));
          } catch (probeErr) {
            // eslint-disable-next-line no-console
            console.error('[ProctoringWidget] Manifest probe failed:', probeErr && (probeErr.message || String(probeErr)));
            setModelDiag(prev => ({ ...prev, manifestStatus: -1 }));
          }
        }
      }
    })();
    return () => { cancelled = true; };
  }, [modelsBaseUrl]);

  // Heartbeat: pre-exam presence during environment check; full monitoring heartbeat when exam is live
  useEffect(() => {
    if (!wsSessionActive || !userId || !examId) return undefined;
    const sendBeat = () => {
      try {
        ws.send('heartbeat', {
          examId: examRoomId,
          userId: String(userId),
          phase: canRunMonitoring ? 'monitoring' : 'pre_exam',
        });
      } catch (_) {}
    };
    sendBeat();
    const offConnect = ws.onConnect(sendBeat);
    const hb = setInterval(sendBeat, 5000);
    return () => {
      offConnect();
      clearInterval(hb);
    };
  }, [wsSessionActive, canRunMonitoring, userId, examId, examRoomId]);

  // Monitoring: face detection + lighting (gated by prerequisites + exam active)
  useEffect(() => {
    if (!canRunMonitoring || !userId || !examId) return;
    if (!modelsLoaded) return; // gate until models ready
    if (started.current) return;
    started.current = true;

    const detectInterval = setInterval(async () => {
      const video = videoRef.current;
      if (!video || !isStreaming || video.videoWidth === 0) return;
      try {
        const detections = await faceapi.detectAllFaces(
          video,
          new faceapi.TinyFaceDetectorOptions({
            scoreThreshold: faceDetectionScoreThreshold,
            inputSize: 416
          })
        );
        const count = detections.length;
        if (count > 0) {
          lastFaceSeenAtRef.current = Date.now();
          if (violationsPaused) {
            setViolationsPaused(false);
            setNoFaceModalOpen(false);
            // eslint-disable-next-line no-console
            console.log('[ProctoringWidget] Face detected again, resuming violation tracking');
          }
        } else {
          const msSince = Date.now() - lastFaceSeenAtRef.current;
          if (msSince > noFaceTimeoutMs && !violationsPaused) {
            await reportViolation({
              type: 'No Face Detected',
              details: `No face visible in camera for more than ${Math.round(noFaceTimeoutMs / 1000)} seconds`,
              severity: 'high'
            });
            setViolationsPaused(true);
            setNoFaceModalOpen(true);
            // eslint-disable-next-line no-console
            console.log('[ProctoringWidget] No face >', noFaceTimeoutMs, 'ms, pausing violation tracking');
          }
        }
        if (!violationsPaused && count > 1) {
          await reportViolation({ type: 'Multiple Faces Detected', details: `${count} faces detected`, severity: 'high' });
        }
      } catch (_) {}

      // Lighting check (only when enabled)
      if (enableLightingMonitoring && !violationsPaused) {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth; canvas.height = video.videoHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0);
          const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const brightness = averageFrameBrightness(img);
          if (showDebug) {
            setSensorDebug((prev) => ({ ...prev, brightness: Math.round(brightness) }));
          }
          if (brightness < lowLightThreshold) {
            await reportViolation({ type: 'Low Lighting Detected', details: 'Environment is too dark – please improve lighting', severity: 'medium' });
          } else if (brightness > highLightThreshold) {
            await reportViolation({ type: 'High Lighting Detected', details: `Environment is too bright (level ${Math.round(brightness)})`, severity: 'low' });
          }
        } catch (_) {}
      }
    }, faceDetectionIntervalMs);

    return () => { clearInterval(detectInterval); started.current = false; };
  }, [canRunMonitoring, userId, examId, examRoomId, isStreaming, onViolation, modelsLoaded, violationsPaused, enableLightingMonitoring, lowLightThreshold, highLightThreshold, faceDetectionScoreThreshold, faceDetectionIntervalMs, noFaceTimeoutMs, showDebug]);

  // Noise monitoring: mic level over threshold -> report violation and show toast
  useEffect(() => {
    if (!canRunMonitoring || !enableNoiseMonitoring) return;
    const state = { audioContext: null, stream: null, ownsStream: false, intervalId: null, cancelled: false };
    (async () => {
      try {
        let stream = mediaStreamRef.current;
        const hasAudioTrack = stream && stream.getAudioTracks().some((t) => t.readyState === 'live');
        if (!hasAudioTrack) {
          stream = await navigator.mediaDevices.getUserMedia({
            audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
          });
          state.ownsStream = true;
        }
        if (state.cancelled) {
          if (state.ownsStream) stream.getTracks().forEach((t) => t.stop());
          return;
        }
        state.stream = stream;
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        state.audioContext = audioContext;
        await ensureAudioContextRunning(audioContext);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 2048;
        analyser.smoothingTimeConstant = 0.65;
        analyser.minDecibels = -90;
        analyser.maxDecibels = -10;
        const source = audioContext.createMediaStreamSource(stream);
        const silentGain = audioContext.createGain();
        silentGain.gain.value = 0;
        source.connect(analyser);
        analyser.connect(silentGain);
        silentGain.connect(audioContext.destination);
        const bufferLength = analyser.frequencyBinCount;
        const frequencyBuffer = new Uint8Array(bufferLength);
        const timeDomainBuffer = new Uint8Array(bufferLength);
        if (showDebug) {
          setSensorDebug((prev) => ({
            ...prev,
            noiseMonitoring: true,
            noiseError: null,
            audioContextState: audioContext.state
          }));
        }
        let consecutiveLoudReadings = 0;
        state.intervalId = setInterval(async () => {
          if (state.cancelled || violationsPausedRef.current) return;
          await ensureAudioContextRunning(audioContext);
          const level = measureAudioLevel(analyser, frequencyBuffer, timeDomainBuffer);
          if (showDebug) {
            setSensorDebug((prev) => ({
              ...prev,
              noiseLevel: Math.round(level),
              audioContextState: audioContext.state
            }));
          }
          if (level >= noiseThreshold) {
            consecutiveLoudReadings += 1;
            if (consecutiveLoudReadings >= noiseConsecutiveReadings) {
              consecutiveLoudReadings = 0;
              // eslint-disable-next-line no-console
              console.log('[Proctoring][Widget] Sustained loud sound detected', {
                level: Math.round(level),
                threshold: noiseThreshold,
                consecutiveReadings: noiseConsecutiveReadings,
              });
              reportViolation({ type: 'Loud Sound Detected', details: `Background noise is too loud (level ${Math.round(level)})`, severity: 'medium' });
            }
          } else {
            consecutiveLoudReadings = 0;
          }
        }, noiseCheckInterval);
      } catch (err) {
        const message = err && (err.message || String(err));
        // eslint-disable-next-line no-console
        console.warn('[ProctoringWidget] Noise monitoring failed (no mic or permission):', err);
        if (showDebug) {
          setSensorDebug((prev) => ({ ...prev, noiseMonitoring: false, noiseError: message }));
        }
      }
    })();
    return () => {
      state.cancelled = true;
      if (state.intervalId) clearInterval(state.intervalId);
      if (state.ownsStream && state.stream) state.stream.getTracks().forEach((t) => t.stop());
      if (state.audioContext) state.audioContext.close().catch(() => {});
      if (showDebug) {
        setSensorDebug((prev) => ({ ...prev, noiseMonitoring: false, noiseLevel: null, audioContextState: null }));
      }
    };
  }, [canRunMonitoring, enableNoiseMonitoring, noiseThreshold, noiseConsecutiveReadings, noiseCheckInterval, isStreaming, showDebug]);

  // Screen sharing detection: browser tab/window capture only (desktop apps like Teams cannot be detected)
  useEffect(() => {
    if (!canRunMonitoring || !userId || !examId) return;
    let cancelled = false;
    const probe = async () => {
      if (cancelled || violationsPaused) return;
      const streams = [];
      if (mediaStreamRef.current) streams.push(mediaStreamRef.current);
      const result = await detectActiveScreenCapture(streams);
      if (showDebug) {
        setSensorDebug((prev) => ({
          ...prev,
          screenCapture: result.detected,
          screenCaptureReason: result.reason
        }));
      }
      if (result.detected) {
        await reportViolation({
          type: 'Screen Sharing Detected',
          details: 'Screen sharing or window capture detected in the browser',
          severity: 'high'
        });
      }
    };
    const interval = setInterval(probe, 5000);
    probe();
    const onDeviceChange = () => { probe(); };
    if (navigator.mediaDevices && navigator.mediaDevices.addEventListener) {
      navigator.mediaDevices.addEventListener('devicechange', onDeviceChange);
    }
    return () => {
      cancelled = true;
      clearInterval(interval);
      if (navigator.mediaDevices && navigator.mediaDevices.removeEventListener) {
        navigator.mediaDevices.removeEventListener('devicechange', onDeviceChange);
      }
    };
  }, [canRunMonitoring, userId, examId, violationsPaused, showDebug]);

  // Network connection: offline event + 30s interval when monitoring
  useEffect(() => {
    if (!canRunMonitoring || !userId || !examId) return;
    const reportNetworkLost = () => {
      if (violationsPaused) return;
      reportViolation({ type: 'Network Connection Lost', details: 'Internet connection lost', severity: 'high' });
    };
    const handleOffline = () => reportNetworkLost();
    window.addEventListener('offline', handleOffline);
    const interval = setInterval(() => {
      if (!navigator.onLine) reportNetworkLost();
    }, 30000);
    return () => {
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, [canRunMonitoring, userId, examId, violationsPaused]);

  // Send low-FPS webcam frames to admin when admin requests feed (allow before Start Monitoring so admin can see student)
  useEffect(() => {
    if (!userId || !examId) return;
    if (!webcamRequested) return;
    if (!isStreaming) return;
    const roomId = webcamFeedExamId != null ? webcamFeedExamId : examRoomId;
    let intervalId;
    const sendFrame = async () => {
      try {
        const frame = await captureCurrentFrame();
        if (frame) {
          ws.send('webcam_frame', { userId, examId: roomId, frame });
        }
      } catch (_) {}
    };
    // ~1 fps
    intervalId = setInterval(sendFrame, 1000);
    // initial tick
    sendFrame();
    return () => { try { clearInterval(intervalId); } catch (_) {} };
  }, [userId, examId, examRoomId, webcamFeedExamId, webcamRequested, isStreaming]);

  // Closed-book screen share → admin (same request as webcam view)
  useEffect(() => {
    if (!userId || !examId || !webcamRequested || !screenShareStream) return undefined;
    const roomId = webcamFeedExamId != null ? webcamFeedExamId : examRoomId;
    const sendScreen = async () => {
      try {
        const frame = await captureJpegFromStream(screenShareStream);
        if (frame) ws.send('screen_frame', { userId, examId: roomId, frame });
      } catch (_) { /* ignore */ }
    };
    sendScreen();
    const intervalId = setInterval(sendScreen, 1000);
    return () => { try { clearInterval(intervalId); } catch (_) {} };
  }, [userId, examId, examRoomId, webcamFeedExamId, webcamRequested, screenShareStream]);

  // Face verification: run immediately when monitoring starts, then every 30 seconds
  useEffect(() => {
    if (!canRunMonitoring || !userId || !examId) return;
    if (violationsPaused) return; // pause when paused
    if (!resolvedApiBaseUrl) {
      // eslint-disable-next-line no-console
      console.warn('[ProctoringWidget] Face verification skipped: apiBaseUrl is not set');
      return;
    }

    const runFaceVerify = async () => {
      const frame = await captureCurrentFrame();
      if (!frame) return;
      faceVerifyRunCountRef.current += 1;
      const isFirstRun = faceVerifyRunCountRef.current === 1;
      let uploadedUrl = null;
      if (uploadSnapshots) {
        uploadedUrl = await uploadSnapshotToS3(frame);
      }
      try {
        const res = await apiFetch(resolvedApiBaseUrl, '/api/verify-face', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ userId, examId: examRoomId, image: frame, skipBroadcast: isFirstRun }) });
        const data = await res.json();
        if (!isFirstRun) {
          setVerifyStats(prev => ({ ok: prev.ok + (data && data.success ? 1 : 0), fail: prev.fail + (data && data.success ? 0 : 1) }));
          // Same payload shape as periodic heartbeat and transcript so server/admin see consistent userId/examId
          const fvPayload = { success: !!(data && data.success), confidence: data && data.confidence != null ? data.confidence : 0 };
          ws.send('face_verify_result', { userId: String(userId), examId: String(examRoomId), success: fvPayload.success, confidence: fvPayload.confidence });
          if (typeof console !== 'undefined' && console.log) {
            console.log('[Proctoring][Widget] SENDING heartbeat WITH faceVerifyResult', fvPayload);
          }
          ws.send('heartbeat', {
            examId: examRoomId,
            userId,
            timestamp: new Date().toISOString(),
            status: 'active',
            faceVerifyResult: fvPayload
          });
          // Optional fallback: POST broadcast (socket is primary; do not depend on this)
          try {
            await apiFetch(resolvedApiBaseUrl, '/api/broadcast-face-verify', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                userId: String(userId),
                examId: String(examRoomId),
                success: fvPayload.success,
                confidence: fvPayload.confidence
              })
            });
          } catch (postErr) {
            if (typeof console !== 'undefined' && console.warn) {
              console.warn('[Proctoring][Widget] broadcast-face-verify POST failed (socket is primary)', postErr && (postErr.message || String(postErr)));
            }
          }
        }
        if (!data.success && !isFirstRun) {
          faceVerifyConsecutiveFailuresRef.current += 1;
          // Face Verification Failed violation is recorded by /api/verify-face on the server (admin violations list).
          if (faceVerifyConsecutiveFailuresRef.current >= faceVerifyFailuresBeforeViolation) {
            faceVerifyConsecutiveFailuresRef.current = 0;
          }
        } else if (data && data.success) {
          faceVerifyConsecutiveFailuresRef.current = 0;
        }
      } catch (err) {
        if (!isFirstRun) {
          setVerifyStats(prev => ({ ...prev, fail: prev.fail + 1 }));
          const fvPayload = { success: false, confidence: 0 };
          ws.send('face_verify_result', { userId: String(userId), examId: String(examRoomId), success: false, confidence: 0 });
          if (typeof console !== 'undefined' && console.log) {
            console.log('[Proctoring][Widget] SENDING heartbeat WITH faceVerifyResult (fail)');
          }
          ws.send('heartbeat', {
            examId: examRoomId,
            userId,
            timestamp: new Date().toISOString(),
            status: 'active',
            faceVerifyResult: fvPayload
          });
          try {
            await apiFetch(resolvedApiBaseUrl, '/api/broadcast-face-verify', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ userId: String(userId), examId: String(examRoomId), success: false, confidence: 0 })
            });
          } catch (postErr) {
            if (typeof console !== 'undefined' && console.warn) {
              console.warn('[Proctoring][Widget] broadcast-face-verify POST failed (socket is primary)', postErr && (postErr.message || String(postErr)));
            }
          }
        }
      }
    };

    // Run immediately when monitoring starts
    runFaceVerify();
    // Then every 30 seconds
    const intervalId = setInterval(runFaceVerify, faceVerifyIntervalMs);
    return () => clearInterval(intervalId);
  }, [canRunMonitoring, userId, examId, examRoomId, resolvedApiBaseUrl, uploadSnapshots, sendViolationsToServer, violationsPaused, faceVerifyIntervalMs, faceVerifyFailuresBeforeViolation]);

  // Transcript (webkit speech) minimal (gated by prerequisites + exam active)
  useEffect(() => {
    if (!canRunMonitoring) return;
    if (typeof window === 'undefined') return;
    if (!('webkitSpeechRecognition' in window)) return;
    let stopped = false;
    let recognitionActive = false;

    const R = new window.webkitSpeechRecognition();
    recognitionRef.current = R;
    R.continuous = true;
    R.interimResults = true;
    R.lang = 'en-US';

    const safeStart = () => {
      if (stopped) return;
      if (recognitionActive || recognitionStartingRef.current) return;
      recognitionStartingRef.current = true;
      setTimeout(() => {
        try { if (!stopped && !recognitionActive) R.start(); } catch (_) {}
        recognitionStartingRef.current = false;
      }, 100);
    };

    const sendFinalTranscript = async (text) => {
      const trimmed = (text || '').trim();
      if (!trimmed) return;
      if (lastFinalTranscriptRef.current === trimmed) return; // avoid duplicates
      lastFinalTranscriptRef.current = trimmed;
      // Send via WebSocket for real-time admin delivery
      try {
        ws.send('transcript', {
          userId,
          examId: examRoomId,
          transcript: trimmed,
          text: trimmed,
          ...(resolvedIacId != null ? { iacId: resolvedIacId } : {})
        });
      } catch (_) {}
      try {
        await apiFetch(resolvedApiBaseUrl, '/api/record-transcript', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            userId,
            examId,
            transcript: trimmed,
            ...(resolvedIacId != null ? { iacId: resolvedIacId } : {})
          })
        });
        transcriptRetryRef.current.retries = 0;
      } catch (_) {
        // simple retry with small backoff
        const r = Math.min((transcriptRetryRef.current.retries || 0) + 1, 5);
        transcriptRetryRef.current.retries = r;
        const delay = 300 * r;
        setTimeout(() => {
          try { apiFetch(resolvedApiBaseUrl, '/api/record-transcript', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({
              userId,
              examId,
              transcript: trimmed,
              ...(resolvedIacId != null ? { iacId: resolvedIacId } : {})
            })
          }); } catch (_) {}
        }, delay);
      }
    };

    R.onresult = (event) => {
      const results = Array.from(event.results);
      const transcript = results.map(r => r[0]?.transcript || '').join('');
      const last = results[results.length - 1];
      const isFinal = !!(last && last.isFinal);
      onTranscriptUpdate && onTranscriptUpdate(transcript);
      if (sendTranscriptsToServer && isFinal) {
        sendFinalTranscript(transcript);
      }
    };
    R.onerror = () => { recognitionActive = false; safeStart(); };
    R.onstart = () => { recognitionActive = true; };
    R.onend = () => { recognitionActive = false; safeStart(); };

    safeStart();
    return () => { stopped = true; try { R.stop(); } catch (_) {} };
  }, [canRunMonitoring, onTranscriptUpdate, resolvedApiBaseUrl, userId, examId, examRoomId, sendTranscriptsToServer, resolvedIacId]);

  async function handleCaptureFace() {
    setRegError('');
    setFaceUploading(true);
    try {
      const frame = await captureCurrentFrame();
      if (!frame) throw new Error('Camera not ready');
      const res = await apiFetch(resolvedApiBaseUrl, '/api/register-face', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, examId, image: frame })
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Face upload failed');
      setFaceUploaded(true);
    } catch (e) {
      setRegError(e && e.message ? e.message : 'Failed to capture/upload face. Please try again.');
    } finally {
      setFaceUploading(false);
    }
  }

  function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  async function handleUploadId(file) {
    if (!file) return;
    setRegError('');
    setIdUploading(true);
    try {
      const dataUrl = await fileToDataUrl(file);
      const res = await apiFetch(resolvedApiBaseUrl, '/api/upload-id', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, examId, idImage: dataUrl, image: dataUrl })
      });
      const data = await res.json();
      if (!data.success) throw new Error('ID upload failed');
      setIdUploaded(true);
    } catch (e) {
      setRegError('Failed to upload ID. Please try another image.');
    } finally {
      setIdUploading(false);
    }
  }

  async function runSystemTest() {
    setSysRunning(true);
    const results = { camera: false, models: false, speech: false, backend: false, faceDetected: false, faceCount: 0 };
    try {
      const vid = await waitForVideoReady(() => previewVideoRef.current || videoRef.current);
      if (vid && vid.videoWidth > 0) {
        results.camera = true;
      } else if (isStreaming) {
        results.camera = true;
      } else {
        try {
          const s = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
          s.getTracks().forEach(t => t.stop());
          results.camera = true;
        } catch (_) { results.camera = false; }
      }
      results.models = !!modelsLoaded;
      results.speech = hasSpeechRecognitionSupport();
      try {
        const res = await apiFetch(resolvedApiBaseUrl, '/api/ping');
        const data = await res.json();
        results.backend = !!(data && data.success);
      } catch (_) { results.backend = false; }

      if (results.models) {
        const faceVid = await waitForVideoReady(() => previewVideoRef.current || videoRef.current);
        if (faceVid && faceVid.videoWidth > 0) {
          try {
            const dets = await faceapi.detectAllFaces(faceVid, new faceapi.TinyFaceDetectorOptions());
            results.faceCount = dets.length;
            results.faceDetected = dets.length > 0;
            // eslint-disable-next-line no-console
            console.log('[ProctoringWidget] System test face detection count =', dets.length);
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('[ProctoringWidget] System test face detection error:', e && (e.message || String(e)));
            results.faceDetected = false;
            results.faceCount = 0;
          }
        }
      }
    } finally {
      setSysResults(results);
      setSysRunning(false);
      setHasRunSystemTest(true);
      // eslint-disable-next-line no-console
      console.log('[ProctoringWidget] System test results:', results, 'modelDiag:', modelDiag);
      try {
        // Emit to admin for visibility/recording
        ws.send('system_test_result', { userId, examId: examRoomId, results, timestamp: Date.now() });
      } catch (_) {}
    }
  }

  // Run system test when registration opens on step 0 only if user starts it (not auto on load)
  // Environment check for registered users is scheduled separately before exam start.

  // Run system test once when environment check modal opens (only inside T-N..endExam)
  useEffect(() => {
    if (!systemCheckOpenForRegistered) return undefined;
    if (!isEnvironmentCheckPeriod(examStartAt, endExam, resolvedSystemCheckMinutes, getNowMs())) {
      // eslint-disable-next-line no-console
      console.log('[ProctoringWidget][Schedule] environment check modal closed — outside T-N..endExam window');
      setSystemCheckOpenForRegistered(false);
      return undefined;
    }
    // eslint-disable-next-line no-console
    console.log('[ProctoringWidget][Schedule] running environment system test');
    setHasRunSystemTest(false);
    runSystemTest();
    return undefined;
    // Intentionally only re-run when the modal opens/closes — not on every clock sync (avoids flicker)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [systemCheckOpenForRegistered]);

  async function persistRegistration() {
    if (completingRegistration || !faceUploaded || !idUploaded) return;
    setCompletingRegistration(true);
    setRegError('');
    try {
      const res = await apiFetch(resolvedApiBaseUrl, '/api/complete-proctoring-registration', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, examId })
      });
      if (res.status === 404) {
        setRegError('Registration server endpoint not available. Please contact support. You can continue for now.');
        setRegistrationOpen(false);
        setIsRegistered(true);
        setRegSuccessOpen(true);
        setPrerequisitesComplete(true);
        onRegistrationStatusRef.current && onRegistrationStatusRef.current(true);
        // Do not arm monitoring here — wait for T-N environment check
        setTimeout(() => setRegSuccessOpen(false), 5000);
      } else {
        const data = await res.json();
        if (data.success) {
          setRegistrationOpen(false);
          setIsRegistered(true);
          setRegSuccessOpen(true);
          setPrerequisitesComplete(true);
          onRegistrationStatusRef.current && onRegistrationStatusRef.current(true);
          // Do not call onReadyForMonitoring — env check must complete first
          setTimeout(() => setRegSuccessOpen(false), 5000);
        } else {
          setRegError('Failed to save registration. Please try again.');
        }
      }
    } catch (_) {
      setRegError('Failed to save registration. Please try again.');
    } finally {
      setCompletingRegistration(false);
    }
  }

  // When both face and ID are uploaded, persist to user_registrations so check-registration finds it on next visit
  useEffect(() => {
    if (!registrationOpen || !faceUploaded || !idUploaded) return;
    let cancelled = false;
    setCompletingRegistration(true);
    setRegError('');
    (async () => {
      try {
        const res = await apiFetch(resolvedApiBaseUrl, '/api/complete-proctoring-registration', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId, examId })
        });
        if (cancelled) return;
        if (res.status === 404) {
          setRegError('Registration server endpoint not available. Please contact support. You can continue for now.');
          setRegistrationOpen(false);
          setIsRegistered(true);
          setRegSuccessOpen(true);
          setPrerequisitesComplete(true);
          onRegistrationStatusRef.current && onRegistrationStatusRef.current(true);
          // Do not arm monitoring here — wait for T-N environment check
          setTimeout(() => setRegSuccessOpen(false), 5000);
        } else {
          const data = await res.json();
          if (cancelled) return;
          if (data.success) {
            setRegistrationOpen(false);
            setIsRegistered(true);
            setRegSuccessOpen(true);
            setPrerequisitesComplete(true);
            onRegistrationStatusRef.current && onRegistrationStatusRef.current(true);
            // Do not call onReadyForMonitoring — env check must complete first
            setTimeout(() => setRegSuccessOpen(false), 5000);
          } else {
            setRegError('Failed to save registration. Please try again.');
          }
        }
      } catch (_) {
        if (!cancelled) setRegError('Failed to save registration. Please try again.');
      } finally {
        if (!cancelled) setCompletingRegistration(false);
      }
    })();
    return () => { cancelled = true; };
  }, [registrationOpen, faceUploaded, idUploaded, userId, examId, resolvedApiBaseUrl]);

  const systemPassed = sysResults.camera && sysResults.models && sysResults.backend && sysResults.faceDetected;

  async function persistEnvironmentCheckAudit(results) {
    if (!resolvedApiBaseUrl || !userId || !examId) return false;
    try {
      const auditPayload = {
        userId,
        examId,
        checkType: 'pre_exam',
        browser: true,
        camera: !!results.camera,
        models: !!results.models,
        internet: !!results.backend,
        microphone: !!results.speech,
        noise: true,
        lighting: true,
        allPassed: !!(results.camera && results.models && results.backend && results.faceDetected),
        errorMessage: JSON.stringify({
          phase: 'environment_check',
          faceDetected: results.faceDetected,
          faceCount: results.faceCount,
          speech: results.speech,
          completedAt: new Date(getNowMs()).toISOString(),
        }),
      };
      const res = await apiFetch(resolvedApiBaseUrl, '/api/save-system-check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(auditPayload),
      });
      const data = await res.json();
      return !!(data && data.success);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[ProctoringWidget][Schedule] failed to save environment check audit', err && (err.message || String(err)));
      return false;
    }
  }

  async function completeEnvironmentCheck() {
    if (!systemPassed || sysRunning) return;
    setUserClickedStartMonitoring(true);
    const saved = await persistEnvironmentCheckAudit(sysResults);
    setEnvironmentCheckCompleted(true);
    setEnvironmentCheckDismissed(false);
    setSystemCheckOpenForRegistered(false);
    onSystemCheckComplete && onSystemCheckComplete(true);
    onReadyForMonitoringRef.current && onReadyForMonitoringRef.current(true);
    onStartMonitoringClick && onStartMonitoringClick();
    setToast({
      open: true,
      title: 'Environment check complete',
      text: saved
        ? 'Your setup has been verified and recorded for the exam audit. You will not be asked again.'
        : 'Your setup has been verified. Waiting for the exam to start.',
    });
    // eslint-disable-next-line no-console
    console.log('[ProctoringWidget][Schedule] environment check completed — audit saved:', saved);
    try {
      ws.send('environment_check_complete', {
        userId,
        examId: examRoomId,
        results: sysResults,
        auditSaved: saved,
        timestamp: getNowMs(),
      });
    } catch (_) {}
  }

  function dismissRegistration() {
    setRegistrationOpen(false);
    setRegistrationDismissed(true);
  }

  function dismissEnvironmentCheck() {
    setSystemCheckOpenForRegistered(false);
    setEnvironmentCheckDismissed(true);
  }

  // Always portal to body so panels aren't clipped/hidden inside the client mount wrapper.
  // Use client mount z-index when > 0; otherwise a fallback above normal exam chrome.
  const portalUi = typeof document !== 'undefined' ? document.body : null;
  const layerZ = (hostZIndex != null && hostZIndex > 0) ? hostZIndex : PANEL_Z_FALLBACK;
  const floatingPanelStyle = withZIndex(
    { ...FLOATING_PANEL_FIXED_BASE, boxShadow: '0 12px 40px rgba(0,0,0,0.35)', border: '1px solid rgba(0,0,0,0.12)' },
    layerZ
  );

  // Refresh layer when the modal opens (client may apply mount styles after first paint).
  useEffect(() => {
    if (!systemCheckOpenForRegistered && !registrationOpen) return;
    if (!hostRef.current) return;
    setHostZIndex(resolveHostZIndex(hostRef.current));
    // eslint-disable-next-line no-console
    console.log('[ProctoringWidget][UI] panel layer', {
      hostZIndex: resolveHostZIndex(hostRef.current),
      layerZ: (resolveHostZIndex(hostRef.current) != null && resolveHostZIndex(hostRef.current) > 0)
        ? resolveHostZIndex(hostRef.current)
        : PANEL_Z_FALLBACK,
      showEnvironmentCheck: systemCheckOpenForRegistered,
    });
  }, [systemCheckOpenForRegistered, registrationOpen]);

  const needsInlineVideo = showVideo && canRunMonitoring;
  const videoStyle = needsInlineVideo
    ? withZIndex(
      { position: 'fixed', bottom: 16, left: 16, width: 160, height: 120, borderRadius: 8, pointerEvents: 'auto', objectFit: 'cover' },
      layerZ
    )
    : OFFSCREEN_MEDIA_STYLE;
  const shouldMountVideo = !isExcludedFromMonitoring && (
    registrationOpen
    || (systemCheckOpenForRegistered && inEnvironmentCheckPeriod)
    || keepCameraForArmedSession
    || canRunMonitoring
    || webcamRequested
    || needsInlineVideo
  );

  const reopenBtnStyle = withZIndex(
    {
      position: 'fixed',
      bottom: 16,
      right: 16,
      pointerEvents: 'auto',
      padding: '10px 16px',
      borderRadius: 8,
      border: 'none',
      background: '#1976d2',
      color: '#fff',
      cursor: 'pointer',
      fontWeight: 600,
      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    },
    layerZ
  );

  // Show whenever the scheduler opened it (single source of truth) — avoid extra clock gates hiding it.
  const showEnvironmentCheck = systemCheckOpenForRegistered
    && !environmentCheckCompleted
    && !examHasEnded
    && isRegistered
    && isEnvironmentCheckPeriod(
      examStartAt,
      endExam,
      resolvedSystemCheckMinutes,
      conservativeNowMs(getNowMs(), Date.now())
    );

  if (isExcludedFromMonitoring) {
    return <div ref={hostRef} aria-hidden="true" style={MOUNT_SHELL_STYLE} />;
  }

  return (
    <>
      <div ref={hostRef} aria-hidden="true" style={MOUNT_SHELL_STYLE} />
      {portalUi && shouldMountVideo && ReactDOM.createPortal(
        <video ref={videoRef} autoPlay playsInline muted style={videoStyle} />,
        portalUi
      )}
      {portalUi && toast.open && ReactDOM.createPortal(
        <div style={withZIndex({ position: 'fixed', top: 72, right: 16, background: '#323232', color: '#fff', padding: '10px 12px', borderRadius: 8, boxShadow: '0 6px 18px rgba(0,0,0,0.25)', maxWidth: 320, pointerEvents: 'auto' }, layerZ)}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{toast.title || 'Message'}</div>
          <div style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>{toast.text}</div>
          <div style={{ textAlign: 'right', marginTop: 8 }}>
            <button onClick={() => setToast({ open: false, text: '', title: '' })} style={{ background: 'transparent', border: '1px solid #fff', color: '#fff', padding: '4px 8px', borderRadius: 6, cursor: 'pointer' }}>Close</button>
          </div>
        </div>,
        portalUi
      )}
      {portalUi && showDebug && !registrationOpen && ReactDOM.createPortal(
        <div style={withZIndex({ pointerEvents: 'auto', position: 'fixed', bottom: 16, left: 16, maxWidth: 420, maxHeight: '40vh', overflow: 'auto', background: '#fff', border: '1px solid #1976d2', borderRadius: 8, padding: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }, layerZ)}>
              <strong>Proctoring Widget</strong>
              <div>User: {String(userId)} | Exam: {String(examId)}</div>
              <div>WS: {String(wsUrl)}</div>
              <div>API: {String(resolvedApiBaseUrl || '')}</div>
              <div>Models loaded: {String(!!modelsLoaded)} | modelsBaseUrl: {String(modelsBaseUrl || '/models')}</div>
              <div>prerequisitesComplete: {String(!!prerequisitesComplete)} | wsSession: {String(!!wsSessionActive)} | sessionOpen: {String(!!proctoringSessionOpen)} | canRunMonitoring: {String(!!canRunMonitoring)}</div>
              <div>inEnvironmentCheckPeriod: {String(!!inEnvironmentCheckPeriod)} | envCheckComplete: {String(!!environmentCheckCompleted)} | examHasEnded: {String(!!examHasEnded)}</div>
              <div>Exam live: {String(!!isExamLive)} | waitingToStart: {String(!!examWaitingToStart)} | armed: {String(!!monitoringArmed)}</div>
              <div>examStartAt: {formatExamTimestamp(examStartAt) || String(examStartAt || '—')} | T-{resolvedSystemCheckMinutes}m window | tick: {scheduleTick}</div>
              <div>Verify stats (session): ok={verifyStats.ok} fail={verifyStats.fail}</div>
              <div>
                Sensors: brightness={sensorDebug.brightness ?? '—'} (low &lt; {lowLightThreshold}, high &gt; {highLightThreshold})
                {' '}| noise={sensorDebug.noiseLevel ?? '—'} (threshold &gt;= {noiseThreshold}, mic {sensorDebug.noiseMonitoring ? 'on' : 'off'}{sensorDebug.audioContextState ? `, ctx ${sensorDebug.audioContextState}` : ''}{sensorDebug.noiseError ? `, err ${sensorDebug.noiseError}` : ''})
                {' '}| screenCapture={String(!!sensorDebug.screenCapture)}{sensorDebug.screenCaptureReason ? ` (${sensorDebug.screenCaptureReason})` : ''}
              </div>
              <div style={{ fontSize: 11, color: '#666' }}>
                Screen share detection only works for sharing inside this browser (Chrome tab/window share). Desktop apps (Teams, Zoom app) share outside the browser and cannot be detected.
              </div>
              <div>violationsPaused: {String(!!violationsPaused)} — lighting/noise checks stop while paused (e.g. no face)</div>
              <div>start: {String(!!start)} | runSystemCheck: {String(!!runSystemCheck)} | requireSystemCheck: {String(requireSystemCheck !== false)} | admin feed: {String(!!sendAdminFeed)} | uploadSnapshots: {String(!!uploadSnapshots)} | snapshotOnViolation: {String(!!snapshotOnViolation)}</div>
              <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px solid #ccc' }}>
                <strong>Captured violations (debug)</strong>
                <div style={{ fontSize: 12, marginTop: 4 }}>Total: {capturedViolations.length}</div>
                <button type="button" onClick={() => setCapturedViolations([])} style={{ marginTop: 4, padding: '2px 8px', fontSize: 11, cursor: 'pointer' }}>Clear list</button>
                <div style={{ marginTop: 8, maxHeight: 200, overflow: 'auto', fontSize: 11, fontFamily: 'monospace' }}>
                  {capturedViolations.length === 0 && <div style={{ color: '#666' }}>None yet</div>}
                  {capturedViolations.map((v, i) => (
                    <div key={i} style={{ padding: '4px 0', borderBottom: '1px solid #eee' }}>
                      <span style={{ fontWeight: 600 }}>{v.type}</span> | {v.severity} | {v.timestamp ? new Date(v.timestamp).toLocaleTimeString() : '—'} | id: {v.clientViolationId || '—'} | POST: {v._attemptedSendToServer ? 'yes' : 'no'} | WS: {v._attemptedSendToWs ? 'yes' : 'no'}
                    </div>
                  ))}
                </div>
              </div>
        </div>,
        portalUi
      )}

      {portalUi && registrationOpen && ReactDOM.createPortal(
        <>
          <style>{SPINNER_CSS}</style>
          <div style={{ ...floatingPanelStyle, background: '#fff', borderRadius: 12, width: 'min(600px, 95vw)', boxShadow: '0 12px 40px rgba(0,0,0,0.2)' }}>
            {/* Blue header bar - matches design */}
            <div style={{ background: '#1976d2', color: '#fff', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 18, fontWeight: 600 }}>Complete Registration</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', background: regStep > 0 ? '#4caf50' : (regStep === 0 ? '#fff' : '#e3f2fd'), color: regStep > 0 ? '#fff' : (regStep === 0 ? '#1976d2' : '#1976d2'), fontSize: 14, fontWeight: 600 }}>{regStep > 0 ? '✓' : '1'}</span>
                <span style={{ width: 24, height: 2, background: regStep > 1 ? '#4caf50' : 'rgba(255,255,255,0.5)' }} />
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', background: regStep > 1 ? '#4caf50' : (regStep === 1 ? '#fff' : '#e3f2fd'), color: regStep > 1 ? '#fff' : (regStep === 1 ? '#1976d2' : '#1976d2'), fontSize: 14, fontWeight: 600 }}>{regStep > 1 ? '✓' : '2'}</span>
                <span style={{ width: 24, height: 2, background: regStep > 2 ? '#4caf50' : 'rgba(255,255,255,0.5)' }} />
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', background: regStep === 2 ? '#fff' : '#e3f2fd', color: regStep === 2 ? '#1976d2' : '#1976d2', fontSize: 14, fontWeight: 600 }}>3</span>
                </div>
                <button
                  type="button"
                  onClick={dismissRegistration}
                  aria-label="Close registration"
                  style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: 22, lineHeight: 1, cursor: 'pointer', padding: 4 }}
                >
                  ×
                </button>
              </div>
            </div>
            <div style={{ padding: 24 }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4, color: 'rgba(0,0,0,0.87)' }}>
              {regStep === 0 && 'System Check'}
              {regStep === 1 && 'Face Capture'}
              {regStep === 2 && 'ID Verification'}
            </div>
            <div style={{ fontSize: 14, color: 'rgba(0,0,0,0.6)', marginBottom: 20 }}>
              {regStep === 0 && 'Verify your camera, face detection, and connectivity.'}
              {regStep === 1 && 'Position your face in the frame and capture a clear photo.'}
              {regStep === 2 && 'Upload a clear photo of your ID card or passport.'}
            </div>

            {(regStep === 0 || regStep === 1) && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontWeight: 500, marginBottom: 8 }}>Live preview (position your face in frame)</div>
                <div style={{ background: '#0d47a1', borderRadius: 8, minHeight: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#fff', padding: 24, position: 'relative' }}>
                  <video ref={previewVideoRef} autoPlay playsInline muted style={{ width: '100%', maxWidth: 320, maxHeight: 200, borderRadius: 8, background: '#000' }} />
                  {!previewStreamReady && (
                    <div style={{ position: 'absolute', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                      <div className="pw-spinner" style={{ width: 32, height: 32, border: '3px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', boxSizing: 'border-box' }} />
                      <span>Initializing camera...</span>
                    </div>
                  )}
                </div>
                {regStep === 1 && !previewStreamReady && (
                  <button onClick={enablePreviewStream} type="button" style={{ marginTop: 12, padding: '10px 20px', borderRadius: 8, border: 'none', background: '#fbc02d', color: '#333', cursor: 'pointer', fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 20 }}>📷</span> Enable Camera
                  </button>
                )}
              </div>
            )}
            {regStep === 0 && (
              <div>
                {sysRunning ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 120 }}>
                    <div className="pw-spinner" style={{ width: 24, height: 24, border: '2px solid #e0e0e0', borderTopColor: '#1976d2', borderRadius: '50%', boxSizing: 'border-box' }} />
                    <span style={{ marginLeft: 12 }}>Running system checks...</span>
                  </div>
                ) : !hasRunSystemTest ? (
                  <div style={{ textAlign: 'center', padding: '24px 0' }}>
                    <div style={{ marginBottom: 16, color: 'rgba(0,0,0,0.6)' }}>Run a quick camera and connectivity check to continue registration.</div>
                    <button onClick={runSystemTest} type="button" style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: '#1976d2', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
                      Run System Check
                    </button>
                  </div>
                ) : (
                  <div>
                    {[
                      { pass: sysResults.camera, label: 'Camera', detail: sysResults.camera ? 'Functional' : 'Not working', required: true },
                      { pass: sysResults.models, label: 'Face model', detail: sysResults.models ? 'Loaded' : 'Failed', required: true },
                      { pass: sysResults.faceDetected, label: 'Face detected', detail: sysResults.faceDetected ? `Yes (${sysResults.faceCount})` : 'No face in frame', required: true },
                      { pass: sysResults.speech, label: 'Speech', detail: sysResults.speech ? 'Available' : 'Unavailable (optional)', required: false },
                      { pass: sysResults.backend, label: 'Backend', detail: sysResults.backend ? 'Connected' : 'Offline', required: true }
                    ].map(({ pass, label, detail, required }, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ marginRight: 8, color: pass ? '#2e7d32' : (required ? '#c62828' : '#ed6c02'), fontSize: 20 }}>{pass ? '✓' : '✗'}</span>
                        <span>{label}{detail ? `: ${detail}` : ''}{required && ' (Required)'}</span>
                      </div>
                    ))}
                    {!sysResults.models && (
                      <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.6)', marginTop: 12, padding: 8, background: '#f5f5f5', borderRadius: 4 }}>
                        <div>base: <code>{String(modelDiag.base)}</code></div>
                        <div>error: {modelDiag.error || 'n/a'}</div>
                        <a href={`${(modelDiag.base || modelsBaseUrl || '/models').replace(/\/$/, '')}/tiny_face_detector_model-weights_manifest.json`} target="_blank" rel="noreferrer">Check manifest</a>
                      </div>
                    )}
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 24, paddingTop: 16, borderTop: '1px solid #eee' }}>
                  <button onClick={runSystemTest} disabled={sysRunning} style={{ padding: '10px 16px', borderRadius: 4, border: '1px solid rgba(0,0,0,0.23)', background: '#fff', color: 'rgba(0,0,0,0.87)', cursor: sysRunning ? 'not-allowed' : 'pointer', fontWeight: 500 }}>
                    Retry
                  </button>
                  <button onClick={() => setRegStep(1)} disabled={!systemPassed || sysRunning} style={{ padding: '10px 16px', borderRadius: 4, border: 'none', background: systemPassed && !sysRunning ? '#1976d2' : 'rgba(0,0,0,0.12)', color: systemPassed && !sysRunning ? '#fff' : 'rgba(0,0,0,0.26)', cursor: systemPassed && !sysRunning ? 'pointer' : 'not-allowed', fontWeight: 500 }}>
                    Continue
                  </button>
                </div>
              </div>
            )}

            {regStep === 1 && (
              <div>
                <button onClick={handleCaptureFace} disabled={faceUploading || faceUploaded} style={{ padding: '12px 24px', borderRadius: 8, border: 'none', background: faceUploaded ? '#e8f5e9' : '#1976d2', color: faceUploaded ? '#2e7d32' : '#fff', cursor: faceUploading || faceUploaded ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 15 }}>
                  {faceUploaded ? 'Face Captured ✓' : faceUploading ? 'Uploading…' : 'Capture from Webcam'}
                </button>
                {faceUploaded && <div style={{ display: 'flex', alignItems: 'center', marginTop: 12, color: '#2e7d32', fontWeight: 500 }}><span style={{ marginRight: 4 }}>✓</span> Face captured successfully</div>}
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 28, paddingTop: 20, borderTop: '1px solid #eee' }}>
                  <button onClick={() => setRegStep(0)} style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.23)', background: '#fff', color: 'rgba(0,0,0,0.87)', cursor: 'pointer', fontWeight: 500 }}>Back</button>
                  <button onClick={() => setRegStep(2)} disabled={!faceUploaded} style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: faceUploaded ? '#1976d2' : 'rgba(0,0,0,0.12)', color: faceUploaded ? '#fff' : 'rgba(0,0,0,0.26)', cursor: faceUploaded ? 'pointer' : 'not-allowed', fontWeight: 500 }}>Next</button>
                </div>
              </div>
            )}

            {regStep === 2 && (
              <div>
                <label
                  htmlFor="pw-id-upload"
                  style={{
                    display: 'block',
                    border: '2px dashed #bdbdbd',
                    borderRadius: 12,
                    padding: 40,
                    textAlign: 'center',
                    cursor: idUploading || idUploaded ? 'default' : 'pointer',
                    background: idUploaded ? '#e8f5e9' : (idUploading ? '#f5f5f5' : '#fafafa'),
                    transition: 'background 0.2s, border-color 0.2s'
                  }}
                  onDragOver={(e) => { e.preventDefault(); if (!idUploading && !idUploaded) e.currentTarget.style.borderColor = '#1976d2'; }}
                  onDragLeave={(e) => { e.currentTarget.style.borderColor = '#bdbdbd'; }}
                  onDrop={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = '#bdbdbd'; const f = e.dataTransfer?.files?.[0]; if (f && /^image\//.test(f.type)) handleUploadId(f); }}
                >
                  <input id="pw-id-upload" type="file" accept="image/*" onChange={(e) => handleUploadId(e.target.files?.[0])} disabled={idUploading || idUploaded} style={{ display: 'none' }} />
                  {idUploading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                      <div className="pw-spinner" style={{ width: 32, height: 32, border: '3px solid #e0e0e0', borderTopColor: '#1976d2', borderRadius: '50%', boxSizing: 'border-box' }} />
                      <span>Uploading ID…</span>
                    </div>
                  ) : idUploaded ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, color: '#2e7d32' }}>
                      <span style={{ fontSize: 32 }}>✓</span>
                      <span style={{ fontWeight: 600 }}>ID uploaded successfully</span>
                    </div>
                  ) : (
                    <>
                      <div style={{ fontSize: 48, marginBottom: 12, color: '#9e9e9e' }}>↑</div>
                      <div style={{ fontWeight: 500, marginBottom: 4 }}>Click to upload or drag and drop</div>
                      <div style={{ fontSize: 13, color: 'rgba(0,0,0,0.6)' }}>PNG, JPG up to 10MB</div>
                    </>
                  )}
                </label>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 24, paddingTop: 20, borderTop: '1px solid #eee' }}>
                  <button onClick={() => setRegStep(1)} disabled={completingRegistration} style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.23)', background: '#fff', color: 'rgba(0,0,0,0.87)', cursor: completingRegistration ? 'not-allowed' : 'pointer', fontWeight: 500 }}>Back</button>
                  <button
                    type="button"
                    onClick={persistRegistration}
                    disabled={!(systemPassed && faceUploaded && idUploaded) || completingRegistration}
                    style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: systemPassed && faceUploaded && idUploaded && !completingRegistration ? '#1976d2' : 'rgba(0,0,0,0.12)', color: systemPassed && faceUploaded && idUploaded && !completingRegistration ? '#fff' : 'rgba(0,0,0,0.26)', cursor: systemPassed && faceUploaded && idUploaded && !completingRegistration ? 'pointer' : 'not-allowed', fontWeight: 500 }}
                  >
                    {completingRegistration ? 'Saving…' : 'Finish'}
                  </button>
                </div>
              </div>
            )}

            {regError && <div style={{ display: 'flex', alignItems: 'center', marginTop: 12, padding: 8, color: '#c62828', background: '#ffebee', borderRadius: 4 }}><span style={{ marginRight: 4 }}>✗</span>{regError}</div>}
            </div>
          </div>
        </>,
        portalUi
      )}

      {portalUi && showEnvironmentCheck && ReactDOM.createPortal(
        <>
          <style>{SPINNER_CSS}</style>
          <div style={{ ...floatingPanelStyle, background: '#fff', borderRadius: 8, padding: 24, width: 'min(560px, 95vw)', boxShadow: '0 8px 24px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
              <div>
                <div style={{ fontSize: 20, fontWeight: 600 }}>Environment Check</div>
                <div style={{ fontSize: 14, color: 'rgba(0,0,0,0.6)', marginTop: 4 }}>
                  {isExamLive ? 'Verify your setup to start monitoring' : 'Verify your setup before the exam starts'}
                </div>
              </div>
              <button
                type="button"
                onClick={dismissEnvironmentCheck}
                aria-label="Close environment check"
                style={{ background: 'transparent', border: 'none', color: 'rgba(0,0,0,0.54)', fontSize: 22, lineHeight: 1, cursor: 'pointer', padding: 4 }}
              >
                ×
              </button>
            </div>
            <div style={{ fontSize: 13, color: 'rgba(0,0,0,0.55)', marginBottom: 20 }}>
              Opens {resolvedSystemCheckMinutes} minutes before the exam starts and remains available after start until completed.
            </div>
            <div style={{ marginBottom: 16, background: '#0d47a1', borderRadius: 8, minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              <video ref={previewVideoRef} autoPlay playsInline muted style={{ width: '100%', maxWidth: 320, maxHeight: 180, borderRadius: 8, background: '#000' }} />
            </div>
            {sysRunning ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 120 }}>
                <div className="pw-spinner" style={{ width: 24, height: 24, border: '2px solid #e0e0e0', borderTopColor: '#1976d2', borderRadius: '50%', boxSizing: 'border-box' }} />
                <span style={{ marginLeft: 12 }}>Running system checks...</span>
              </div>
            ) : !hasRunSystemTest ? (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <button onClick={runSystemTest} type="button" style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: '#1976d2', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
                  Run Environment Check
                </button>
              </div>
            ) : (
              <div>
                {[
                  { pass: sysResults.camera, label: 'Camera', detail: sysResults.camera ? 'Functional' : 'Not working', required: true },
                  { pass: sysResults.models, label: 'Face model', detail: sysResults.models ? 'Loaded' : 'Failed', required: true },
                  { pass: sysResults.faceDetected, label: 'Face detected', detail: sysResults.faceDetected ? `Yes (${sysResults.faceCount})` : 'No face in frame', required: true },
                  { pass: sysResults.speech, label: 'Speech', detail: sysResults.speech ? 'Available' : 'Unavailable (optional)', required: false },
                  { pass: sysResults.backend, label: 'Backend', detail: sysResults.backend ? 'Connected' : 'Offline', required: true }
                ].map(({ pass, label, detail, required }, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ marginRight: 8, color: pass ? '#2e7d32' : (required ? '#c62828' : '#ed6c02'), fontSize: 20 }}>{pass ? '✓' : '✗'}</span>
                    <span>{label}{detail ? `: ${detail}` : ''}{required && ' (Required)'}</span>
                  </div>
                ))}
                {!sysResults.models && (
                  <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.6)', marginTop: 12, padding: 8, background: '#f5f5f5', borderRadius: 4 }}>
                    <div>base: <code>{String(modelDiag.base)}</code></div>
                    <div>error: {modelDiag.error || 'n/a'}</div>
                    <a href={`${(modelDiag.base || modelsBaseUrl || '/models').replace(/\/$/, '')}/tiny_face_detector_model-weights_manifest.json`} target="_blank" rel="noreferrer">Check manifest</a>
                  </div>
                )}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 24, paddingTop: 16, borderTop: '1px solid #eee' }}>
              <button onClick={runSystemTest} disabled={sysRunning} style={{ padding: '10px 16px', borderRadius: 4, border: '1px solid rgba(0,0,0,0.23)', background: '#fff', color: 'rgba(0,0,0,0.87)', cursor: sysRunning ? 'not-allowed' : 'pointer', fontWeight: 500 }}>
                Retry
              </button>
              <button
                onClick={completeEnvironmentCheck}
                disabled={!systemPassed || sysRunning}
                type="button"
                style={{ padding: '10px 16px', borderRadius: 4, border: 'none', background: systemPassed && !sysRunning ? '#1976d2' : 'rgba(0,0,0,0.12)', color: systemPassed && !sysRunning ? '#fff' : 'rgba(0,0,0,0.26)', cursor: systemPassed && !sysRunning ? 'pointer' : 'not-allowed', fontWeight: 500 }}
              >
                {isExamLive ? 'Start Monitoring' : 'Ready for Exam'}
              </button>
            </div>
          </div>
        </>,
        portalUi
      )}

      {portalUi && regSuccessOpen && ReactDOM.createPortal(
        <div style={withZIndex({ position: 'fixed', top: 72, right: 16, background: '#fff', borderRadius: 8, padding: 20, width: 'min(420px, 95vw)', textAlign: 'center', boxShadow: '0 8px 24px rgba(0,0,0,0.2)', pointerEvents: 'auto' }, layerZ)}>
          <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2e7d32' }}><span style={{ marginRight: 8 }}>✓</span> Registration Complete</div>
          <div style={{ fontSize: 14, color: 'rgba(0,0,0,0.6)', marginBottom: 16 }}>
            Your face and ID have been uploaded. The environment check opens {resolvedSystemCheckMinutes} minutes before the exam (and after start if not completed yet). Monitoring will not begin until that check is done and the exam is live.
          </div>
          <button
            onClick={() => setRegSuccessOpen(false)}
            style={{ padding: '8px 20px', borderRadius: 4, border: '1px solid rgba(0,0,0,0.23)', background: '#fff', color: 'rgba(0,0,0,0.87)', cursor: 'pointer', fontWeight: 500 }}
          >
            Close
          </button>
        </div>,
        portalUi
      )}

      {portalUi && noFaceModalOpen && ReactDOM.createPortal(
        <div style={withZIndex({ position: 'fixed', top: 72, left: 16, background: '#fff', borderRadius: 8, padding: 24, width: 'min(460px, 95vw)', textAlign: 'center', boxShadow: '0 8px 24px rgba(0,0,0,0.2)', pointerEvents: 'auto' }, layerZ)}>
            <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>Face Not Detected</div>
            <div style={{ fontSize: 14, color: 'rgba(0,0,0,0.6)', marginBottom: 8 }}>
              We can't see your face. Violation tracking is paused. Please return to the camera to continue.
            </div>
            <div style={{ fontSize: 13, color: 'rgba(0,0,0,0.5)', marginBottom: 24 }}>Audio transcription will continue.</div>
            <button onClick={() => setNoFaceModalOpen(false)} style={{ padding: '10px 24px', borderRadius: 4, border: '1px solid #1976d2', background: '#fff', color: '#1976d2', cursor: 'pointer', fontWeight: 500 }}>OK</button>
        </div>,
        portalUi
      )}

      {portalUi && !registrationOpen && !isRegistered && registrationDismissed && ReactDOM.createPortal(
        <button
          type="button"
          onClick={() => { setRegistrationDismissed(false); setRegistrationOpen(true); setRegStep(0); }}
          style={reopenBtnStyle}
        >
          Complete Registration
        </button>,
        portalUi
      )}

      {portalUi && isRegistered && !systemCheckOpenForRegistered && !environmentCheckCompleted && inEnvironmentCheckPeriod && environmentCheckDismissed && !examHasEnded && ReactDOM.createPortal(
        <button
          type="button"
          onClick={() => {
            if (!isEnvironmentCheckPeriod(examStartAt, endExam, resolvedSystemCheckMinutes, getNowMs())) return;
            setEnvironmentCheckDismissed(false);
            setSystemCheckOpenForRegistered(true);
          }}
          style={reopenBtnStyle}
        >
          Open Environment Check
        </button>,
        portalUi
      )}
    </>
  );
});

ProctoringWidget.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  examId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  adminExamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  libraryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  websocketUrl: PropTypes.string,
  apiBaseUrl: PropTypes.string,
  start: PropTypes.bool,
  runSystemCheck: PropTypes.bool,
  requireSystemCheck: PropTypes.bool,
  onRegistrationStatus: PropTypes.func,
  onSystemCheckComplete: PropTypes.func,
  onViolation: PropTypes.func,
  onTranscriptUpdate: PropTypes.func,
  sendAdminFeed: PropTypes.bool,
  uploadSnapshots: PropTypes.bool,
  snapshotOnViolation: PropTypes.bool,
  modelsBaseUrl: PropTypes.string,
  examIsActive: PropTypes.bool,
  sendViolationsToServer: PropTypes.bool,
  sendTranscriptsToServer: PropTypes.bool,
  examStartAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  endExam: PropTypes.bool,
  systemCheckMinutesBeforeExam: PropTypes.number,
  showDebug: PropTypes.bool,
  showVideo: PropTypes.bool,
  onReadyForMonitoring: PropTypes.func,
  onStartMonitoringClick: PropTypes.func,
  enableNoiseMonitoring: PropTypes.bool,
  noiseThreshold: PropTypes.number,
  noiseConsecutiveReadings: PropTypes.number,
  noiseCheckInterval: PropTypes.number,
  enableLightingMonitoring: PropTypes.bool,
  lowLightThreshold: PropTypes.number,
  highLightThreshold: PropTypes.number,
  lightingCheckInterval: PropTypes.number,
  faceDetectionScoreThreshold: PropTypes.number,
  faceDetectionIntervalMs: PropTypes.number,
  noFaceTimeoutMs: PropTypes.number,
  faceVerifyIntervalMs: PropTypes.number,
  faceVerifyFailuresBeforeViolation: PropTypes.number,
  iacId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  screenShareStream: PropTypes.object
};

ProctoringWidget.defaultProps = {
  requireSystemCheck: true,
  sendAdminFeed: false,
  uploadSnapshots: true,
  snapshotOnViolation: true,
  modelsBaseUrl: '/models',
  sendViolationsToServer: true,
  sendTranscriptsToServer: true,
  systemCheckMinutesBeforeExam: SYSTEM_CHECK_MINUTES_BEFORE_EXAM_DEFAULT,
  showDebug: false,
  showVideo: false,
  endExam: false,
};

export default ProctoringWidget;


